import { useState, useEffect, useRef } from "react";

// ─── THEMES ────────────────────────────────────────────────────────────────────
const THEMES = [
  {
    id: "sleep", label: "Deep Sleep", emoji: "🌙", tagline: "Drift into stillness",
    quote: "Sleep is the golden chain that ties health and our bodies together.", quoteAuthor: "Thomas Dekker",
    bg: "radial-gradient(ellipse at 30% 20%, #1a1040 0%, #0a0618 40%, #060310 100%)",
    accent: "#8B7FD4", accentGlow: "#6C63FF", textColor: "#D4CFE8", mutedColor: "#7B7399",
    cardBg: "rgba(10,6,24,0.55)", borderColor: "rgba(139,127,212,0.18)", animationType: "sleep",
    soundId: "binaural",
  },
  {
    id: "meditate", label: "Meditate", emoji: "🧘", tagline: "Breathe. Be here now.",
    quote: "The mind is everything. What you think, you become.", quoteAuthor: "Buddha",
    bg: "radial-gradient(ellipse at 50% 0%, #1d3528 0%, #0e2018 50%, #080f0c 100%)",
    accent: "#5DB996", accentGlow: "#3DCC8A", textColor: "#C8E6D8", mutedColor: "#6B9E85",
    cardBg: "rgba(6,14,8,0.55)", borderColor: "rgba(93,185,150,0.18)", animationType: "meditate",
    soundId: "bowl",
  },
  {
    id: "study", label: "Deep Study", emoji: "📚", tagline: "Lock in. Enter the zone.",
    quote: "The secret of getting ahead is getting started.", quoteAuthor: "Mark Twain",
    bg: "radial-gradient(ellipse at 80% 10%, #1a1200 0%, #0f0c00 50%, #080600 100%)",
    accent: "#F0C040", accentGlow: "#FFD700", textColor: "#F0E8C4", mutedColor: "#8A7E50",
    cardBg: "rgba(12,10,0,0.55)", borderColor: "rgba(240,192,64,0.18)", animationType: "study",
    soundId: "brownnoise",
  },
  {
    id: "calm", label: "Ocean Calm", emoji: "🌊", tagline: "Let the waves carry it away.",
    quote: "You can't stop the waves, but you can learn to surf.", quoteAuthor: "Jon Kabat-Zinn",
    bg: "radial-gradient(ellipse at 50% 100%, #003652 0%, #001e35 50%, #000d1a 100%)",
    accent: "#29B6F6", accentGlow: "#0DD3FF", textColor: "#B3E5FC", mutedColor: "#5B9EC9",
    cardBg: "rgba(0,12,24,0.55)", borderColor: "rgba(41,182,246,0.18)", animationType: "ocean",
    soundId: "ocean",
  },
  {
    id: "love", label: "Self Love", emoji: "💖", tagline: "You are worthy of everything.",
    quote: "To love oneself is the beginning of a lifelong romance.", quoteAuthor: "Oscar Wilde",
    bg: "radial-gradient(ellipse at 50% 30%, #3d0a20 0%, #200510 50%, #0f0008 100%)",
    accent: "#FF6B9D", accentGlow: "#FF3D7F", textColor: "#FFD6E7", mutedColor: "#A05575",
    cardBg: "rgba(20,4,12,0.55)", borderColor: "rgba(255,107,157,0.18)", animationType: "love",
    soundId: "love528",
  },
  {
    id: "energy", label: "Fire Energy", emoji: "⚡", tagline: "Ignite. Rise. Conquer.",
    quote: "Energy and persistence conquer all things.", quoteAuthor: "Benjamin Franklin",
    bg: "radial-gradient(ellipse at 50% 100%, #3d1500 0%, #1f0a00 50%, #0a0400 100%)",
    accent: "#FF6B2B", accentGlow: "#FF4500", textColor: "#FFE0C8", mutedColor: "#9A5535",
    cardBg: "rgba(18,6,0,0.55)", borderColor: "rgba(255,107,43,0.18)", animationType: "fire",
    soundId: "fire",
  },
  {
    id: "focus", label: "Flow State", emoji: "🎯", tagline: "Everything else disappears.",
    quote: "The successful warrior is the average man with laser-like focus.", quoteAuthor: "Bruce Lee",
    bg: "radial-gradient(ellipse at 20% 50%, #001a2e 0%, #000d1a 60%, #000508 100%)",
    accent: "#00E5CC", accentGlow: "#00FFF0", textColor: "#B2F5EF", mutedColor: "#3A8E85",
    cardBg: "rgba(0,8,14,0.55)", borderColor: "rgba(0,229,204,0.18)", animationType: "flow",
    soundId: "gamma",
  },
  {
    id: "nature", label: "Forest", emoji: "🌿", tagline: "Ground yourself in the wild.",
    quote: "In every walk with nature, one receives far more than he seeks.", quoteAuthor: "John Muir",
    bg: "radial-gradient(ellipse at 50% 0%, #0a1f0a 0%, #061208 60%, #020603 100%)",
    accent: "#7BC67E", accentGlow: "#4CAF50", textColor: "#C8E6C9", mutedColor: "#5A8A5C",
    cardBg: "rgba(3,8,3,0.55)", borderColor: "rgba(123,198,126,0.18)", animationType: "forest",
    soundId: "forest",
  },
  {
    id: "rain", label: "Rainy Day", emoji: "🌧️", tagline: "Let it pour. Let it cleanse.",
    quote: "Some people feel the rain. Others just get wet.", quoteAuthor: "Bob Marley",
    bg: "radial-gradient(ellipse at 50% 0%, #1a2535 0%, #0d1520 60%, #050a10 100%)",
    accent: "#90A4AE", accentGlow: "#B0BEC5", textColor: "#CFD8DC", mutedColor: "#607D8B",
    cardBg: "rgba(6,10,16,0.55)", borderColor: "rgba(144,164,174,0.18)", animationType: "rain",
    soundId: "rain",
  },
  {
    id: "cosmos", label: "Cosmos", emoji: "✨", tagline: "You are made of stardust.",
    quote: "We are all connected to each other, biologically. To the earth, chemically. To the rest of the universe, atomically.", quoteAuthor: "Neil deGrasse Tyson",
    bg: "radial-gradient(ellipse at 50% 50%, #0a0a2e 0%, #050515 60%, #020208 100%)",
    accent: "#C5B8FF", accentGlow: "#A78BFA", textColor: "#EDE9FE", mutedColor: "#7C6DB5",
    cardBg: "rgba(4,4,18,0.55)", borderColor: "rgba(197,184,255,0.18)", animationType: "cosmos",
    soundId: "cosmos",
  },
];

// ─── WEB AUDIO SYNTHESIZERS ────────────────────────────────────────────────────
function createAudioContext() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  return new Ctx();
}

function makeNoise(ctx, seconds, type = "white") {
  const sr = ctx.sampleRate;
  const buf = ctx.createBuffer(2, sr * seconds, sr);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    if (type === "brown") {
      let last = 0;
      for (let i = 0; i < d.length; i++) {
        const w = Math.random() * 2 - 1;
        last = (last + 0.02 * w) / 1.02;
        d[i] = Math.max(-1, Math.min(1, last * 4));
      }
    } else if (type === "pink") {
      let b0=0,b1=0,b2=0,b3=0,b4=0,b5=0,b6=0;
      for (let i=0;i<d.length;i++){const w=Math.random()*2-1;b0=0.99886*b0+w*0.0555179;b1=0.99332*b1+w*0.0750759;b2=0.96900*b2+w*0.1538520;b3=0.86650*b3+w*0.3104856;b4=0.55000*b4+w*0.5329522;b5=-0.7616*b5-w*0.0168980;d[i]=(b0+b1+b2+b3+b4+b5+b6+w*0.5362)*0.11;b6=w*0.115926;}
    } else {
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    }
  }
  const src = ctx.createBufferSource(); src.buffer = buf; src.loop = true;
  return src;
}

function osc(ctx, freq, type = "sine") {
  const o = ctx.createOscillator(); o.type = type; o.frequency.value = freq; return o;
}

function gain(ctx, val) {
  const g = ctx.createGain(); g.gain.value = val; return g;
}

function bpf(ctx, freq, q = 1) {
  const f = ctx.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = freq; f.Q.value = q; return f;
}

function lpf(ctx, freq) {
  const f = ctx.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = freq; return f;
}

function hpf(ctx, freq) {
  const f = ctx.createBiquadFilter(); f.type = "highpass"; f.frequency.value = freq; return f;
}

function smoothStop(out, ctx, ms = 2500) {
  try { out.gain.linearRampToValueAtTime(0, ctx.currentTime + ms/1000); } catch(e){}
  return setTimeout(() => { try { ctx.close(); } catch(e){} }, ms + 300);
}

function synthBinaural(vol) {
  const ctx = createAudioContext();
  const out = ctx.createGain();
  out.gain.setValueAtTime(0, ctx.currentTime);
  out.gain.linearRampToValueAtTime(vol * 0.6, ctx.currentTime + 5);
  out.connect(ctx.destination);
  const timers = [];
  const merger = ctx.createChannelMerger(2);
  merger.connect(out);
  const lO = osc(ctx, 100); const rO = osc(ctx, 104);
  const lG = gain(ctx, 0.45); const rG = gain(ctx, 0.45);
  lO.connect(lG); lG.connect(merger, 0, 0);
  rO.connect(rG); rG.connect(merger, 0, 1);
  lO.start(); rO.start();
  function subPulse() {
    if (ctx.state === "closed") return;
    const s = osc(ctx, 55); const g = gain(ctx, 0);
    s.connect(g); g.connect(out);
    g.gain.linearRampToValueAtTime(vol * 0.35, ctx.currentTime + 0.8);
    g.gain.linearRampToValueAtTime(0, ctx.currentTime + 3.5);
    s.start(); s.stop(ctx.currentTime + 4);
    timers.push(setTimeout(subPulse, 4000));
  }
  subPulse();
  const padChords = [[220, 277, 330], [175, 220, 262], [130, 165, 196], [196, 247, 294]];
  let padIdx = 0;
  const padNodes = [];
  function launchPad() {
    if (ctx.state === "closed") return;
    padNodes.forEach(n => { try { n.gain.linearRampToValueAtTime(0, ctx.currentTime + 4); } catch(e){} });
    padNodes.length = 0;
    padChords[padIdx].forEach(f => {
      const o = osc(ctx, f + (Math.random() * 1.5 - 0.75));
      const g = gain(ctx, 0);
      const lfo = osc(ctx, 0.04 + Math.random() * 0.03);
      const lG = gain(ctx, f * 0.003);
      lfo.connect(lG); lG.connect(o.frequency); lfo.start();
      o.connect(g); g.connect(out);
      g.gain.linearRampToValueAtTime(vol * 0.07, ctx.currentTime + 4);
      o.start();
      padNodes.push(g);
    });
    padIdx = (padIdx + 1) % padChords.length;
    timers.push(setTimeout(launchPad, 12000));
  }
  launchPad();
  function shimmer() {
    if (ctx.state === "closed") return;
    if (Math.random() > 0.5) { timers.push(setTimeout(shimmer, 8000 + Math.random() * 6000)); return; }
    const freq = [880, 1320, 1760, 2200][Math.floor(Math.random() * 4)];
    const o = osc(ctx, freq); const g = gain(ctx, 0);
    o.connect(g); g.connect(out);
    g.gain.linearRampToValueAtTime(vol * 0.05, ctx.currentTime + 0.1);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 5);
    o.start(); o.stop(ctx.currentTime + 6);
    timers.push(setTimeout(shimmer, 7000 + Math.random() * 8000));
  }
  shimmer();
  return {
    stop: () => { timers.forEach(t => clearTimeout(t)); smoothStop(out, ctx, 3000); },
    setVol: (v) => { out.gain.setTargetAtTime(v * 0.6, ctx.currentTime, 0.15); }
  };
}

function synthBowl(vol) {
  const ctx = createAudioContext();
  const out = ctx.createGain();
  out.gain.setValueAtTime(0, ctx.currentTime);
  out.gain.linearRampToValueAtTime(vol * 0.55, ctx.currentTime + 3);
  out.connect(ctx.destination);
  const timers = [];
  const revBuf = ctx.createBuffer(2, ctx.sampleRate * 5, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = revBuf.getChannelData(ch);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 1.8);
  }
  const rev = ctx.createConvolver(); rev.buffer = revBuf;
  const revG = gain(ctx, 0.4); rev.connect(revG); revG.connect(out);
  function strikeBowl(rootHz = 220, intensity = 1.0) {
    if (ctx.state === "closed") return;
    [[1, 1.0], [2, 0.5], [2.756, 0.3], [3.6, 0.15], [4.53, 0.07]].forEach(([ratio, amp]) => {
      const o = osc(ctx, rootHz * ratio);
      const g = gain(ctx, 0);
      const decayTime = (8 + Math.random() * 4) / ratio;
      g.gain.setValueAtTime(amp * vol * intensity * 0.45, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + decayTime);
      o.connect(g); g.connect(out); o.connect(g); g.connect(rev);
      o.start(); o.stop(ctx.currentTime + decayTime + 0.1);
    });
  }
  function bowlSequence() {
    if (ctx.state === "closed") return;
    strikeBowl(440, 0.6);
    timers.push(setTimeout(() => strikeBowl(220, 1.0), 3000));
    timers.push(setTimeout(bowlSequence, 14000 + Math.random() * 6000));
  }
  strikeBowl(220, 0.8);
  timers.push(setTimeout(bowlSequence, 8000));
  const om = osc(ctx, 136.1); const omG = gain(ctx, 0);
  const omVib = osc(ctx, 0.08); const omVibG = gain(ctx, 0.4);
  omVib.connect(omVibG); omVibG.connect(om.frequency);
  omVib.start(); om.connect(omG); omG.connect(out); om.start();
  function omBreath() {
    if (ctx.state === "closed") return;
    omG.gain.linearRampToValueAtTime(vol * 0.12, ctx.currentTime + 4);
    omG.gain.linearRampToValueAtTime(vol * 0.03, ctx.currentTime + 10);
    timers.push(setTimeout(omBreath, 14000));
  }
  omBreath();
  function bassBoom(delay = 0) {
    if (ctx.state === "closed") return;
    const b = osc(ctx, 55); const bG = gain(ctx, 0);
    b.connect(bG); bG.connect(out);
    bG.gain.setValueAtTime(0, ctx.currentTime + delay);
    bG.gain.linearRampToValueAtTime(vol * 0.25, ctx.currentTime + delay + 0.05);
    bG.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 2.5);
    b.start(ctx.currentTime + delay); b.stop(ctx.currentTime + delay + 3);
  }
  bassBoom(); timers.push(setTimeout(() => bassBoom(), 8000));
  return {
    stop: () => { timers.forEach(t => clearTimeout(t)); smoothStop(out, ctx, 3000); },
    setVol: (v) => { out.gain.setTargetAtTime(v * 0.55, ctx.currentTime, 0.15); }
  };
}

function synthBrownNoise(vol) {
  const ctx = createAudioContext();
  const out = ctx.createGain();
  out.gain.setValueAtTime(0, ctx.currentTime);
  out.gain.linearRampToValueAtTime(vol * 0.65, ctx.currentTime + 2);
  out.connect(ctx.destination);
  const timers = [];
  const noise = makeNoise(ctx, 8, "brown");
  const nLpf = lpf(ctx, 600); const nG = gain(ctx, vol * 0.5);
  noise.connect(nLpf); nLpf.connect(nG); nG.connect(out); noise.start();
  const bassNotes = [110, 110, 98, 98, 117, 117, 110, 130];
  const beatDuration = 0.45;
  let beatIdx = 0;
  function bassHit() {
    if (ctx.state === "closed") return;
    const freq = bassNotes[beatIdx % bassNotes.length];
    const b = osc(ctx, freq, "triangle");
    const bG = gain(ctx, 0);
    const bLpf = lpf(ctx, 300);
    b.connect(bLpf); bLpf.connect(bG); bG.connect(out);
    bG.gain.setValueAtTime(vol * 0.28, ctx.currentTime);
    bG.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + beatDuration * 0.85);
    b.start(); b.stop(ctx.currentTime + beatDuration);
    beatIdx++;
    timers.push(setTimeout(bassHit, beatDuration * 1000));
  }
  timers.push(setTimeout(bassHit, 500));
  let kickCount = 0;
  function kick() {
    if (ctx.state === "closed") return;
    if (kickCount % 4 === 0) {
      const k = osc(ctx, 80); const kG = gain(ctx, 0);
      k.frequency.setValueAtTime(80, ctx.currentTime);
      k.frequency.exponentialRampToValueAtTime(20, ctx.currentTime + 0.12);
      kG.gain.setValueAtTime(vol * 0.4, ctx.currentTime);
      kG.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
      k.connect(kG); kG.connect(out); k.start(); k.stop(ctx.currentTime + 0.2);
    }
    if (kickCount % 2 === 1) {
      const hNoise = makeNoise(ctx, 0.1, "white");
      const hHpf = hpf(ctx, 8000); const hG = gain(ctx, 0);
      hNoise.connect(hHpf); hHpf.connect(hG); hG.connect(out);
      hG.gain.setValueAtTime(vol * 0.06, ctx.currentTime);
      hG.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
      hNoise.start(); hNoise.stop(ctx.currentTime + 0.06);
    }
    kickCount++;
    timers.push(setTimeout(kick, beatDuration * 1000));
  }
  timers.push(setTimeout(kick, 600));
  function chordStab() {
    if (ctx.state === "closed") return;
    [[220, 277, 330], [196, 247, 294], [175, 220, 262]].forEach((chord, ci) => {
      timers.push(setTimeout(() => {
        chord.forEach(f => {
          const o = osc(ctx, f); const g = gain(ctx, 0);
          const lf = lpf(ctx, 1200);
          o.connect(lf); lf.connect(g); g.connect(out);
          g.gain.setValueAtTime(vol * 0.06, ctx.currentTime);
          g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.8);
          o.start(); o.stop(ctx.currentTime + 2);
        });
      }, ci * beatDuration * 8 * 1000));
    });
    timers.push(setTimeout(chordStab, beatDuration * 32 * 1000));
  }
  timers.push(setTimeout(chordStab, 2000));
  const gmrg = ctx.createChannelMerger(2);
  const gL = osc(ctx, 160); const gR = osc(ctx, 200);
  const glG = gain(ctx, vol * 0.04); const grG = gain(ctx, vol * 0.04);
  gL.connect(glG); glG.connect(gmrg, 0, 0);
  gR.connect(grG); grG.connect(gmrg, 0, 1);
  gmrg.connect(out); gL.start(); gR.start();
  return {
    stop: () => { timers.forEach(t => clearTimeout(t)); smoothStop(out, ctx, 2000); },
    setVol: (v) => { out.gain.setTargetAtTime(v * 0.65, ctx.currentTime, 0.1); }
  };
}

function synthOcean(vol) {
  const ctx = createAudioContext();
  const out = ctx.createGain();
  out.gain.setValueAtTime(0, ctx.currentTime);
  out.gain.linearRampToValueAtTime(vol * 0.7, ctx.currentTime + 4);
  out.connect(ctx.destination);
  const timers = [];
  function waveLayer(loHz, hiHz, baseVol, lfoRate, lfoDepth) {
    const noise = makeNoise(ctx, 6, "white");
    const lo = lpf(ctx, hiHz); const hi = hpf(ctx, loHz);
    const g = gain(ctx, baseVol);
    noise.connect(lo); lo.connect(hi); hi.connect(g); g.connect(out); noise.start();
    const waveLfo = osc(ctx, lfoRate); const wlG = gain(ctx, lfoDepth);
    waveLfo.connect(wlG); wlG.connect(g.gain); waveLfo.start();
    return g;
  }
  waveLayer(20,  200,  vol * 0.45, 0.07, vol * 0.3);
  waveLayer(200, 900,  vol * 0.38, 0.10, vol * 0.25);
  waveLayer(900, 5000, vol * 0.28, 0.13, vol * 0.18);
  const bassOsc = osc(ctx, 50); const bassG = gain(ctx, vol * 0.35);
  const bassLfo = osc(ctx, 0.04); const bLfoG = gain(ctx, 8);
  bassLfo.connect(bLfoG); bLfoG.connect(bassOsc.frequency);
  bassLfo.start(); bassOsc.connect(bassG); bassG.connect(out); bassOsc.start();
  function waveCrash() {
    if (ctx.state === "closed") return;
    const noise = makeNoise(ctx, 4, "white");
    const filt = bpf(ctx, 800, 0.5);
    const g = gain(ctx, 0);
    noise.connect(filt); filt.connect(g); g.connect(out);
    g.gain.linearRampToValueAtTime(vol * 0.65, ctx.currentTime + 2.5);
    g.gain.linearRampToValueAtTime(vol * 0.25, ctx.currentTime + 3.5);
    g.gain.linearRampToValueAtTime(0, ctx.currentTime + 7);
    noise.start(); noise.stop(ctx.currentTime + 7.5);
    const foam = makeNoise(ctx, 3, "white");
    const foamF = hpf(ctx, 3000); const foamG = gain(ctx, 0);
    foam.connect(foamF); foamF.connect(foamG); foamG.connect(out);
    foamG.gain.setValueAtTime(vol * 0.15, ctx.currentTime + 3);
    foamG.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 9);
    foam.start(ctx.currentTime + 3); foam.stop(ctx.currentTime + 9);
    timers.push(setTimeout(waveCrash, 12000 + Math.random() * 8000));
  }
  timers.push(setTimeout(waveCrash, 4000));
  function bubble() {
    if (ctx.state === "closed") return;
    if (Math.random() > 0.5) { timers.push(setTimeout(bubble, 1000 + Math.random() * 2000)); return; }
    const o = osc(ctx, 400 + Math.random() * 400); const g = gain(ctx, 0);
    o.frequency.linearRampToValueAtTime(o.frequency.value * 1.8, ctx.currentTime + 0.08);
    g.gain.setValueAtTime(vol * 0.04, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
    o.connect(g); g.connect(out); o.start(); o.stop(ctx.currentTime + 0.12);
    timers.push(setTimeout(bubble, 800 + Math.random() * 2500));
  }
  bubble();
  return {
    stop: () => { timers.forEach(t => clearTimeout(t)); smoothStop(out, ctx, 3000); },
    setVol: (v) => { out.gain.setTargetAtTime(v * 0.7, ctx.currentTime, 0.15); }
  };
}

function synthLove528(vol) {
  const ctx = createAudioContext();
  const out = ctx.createGain();
  out.gain.setValueAtTime(0, ctx.currentTime);
  out.gain.linearRampToValueAtTime(vol * 0.5, ctx.currentTime + 5);
  out.connect(ctx.destination);
  const timers = [];
  const coreFreqs = [[528, 0.5], [1056, 0.2], [264, 0.3], [1584, 0.07], [132, 0.15]];
  const coreNodes = coreFreqs.map(([f, amp]) => {
    const o = osc(ctx, f); const g = gain(ctx, 0);
    const lfo = osc(ctx, 0.05 + Math.random() * 0.04);
    const lG = gain(ctx, f * 0.002);
    lfo.connect(lG); lG.connect(o.frequency); lfo.start();
    o.connect(g); g.connect(out); o.start();
    g.gain.linearRampToValueAtTime(amp * vol * 0.2, ctx.currentTime + 5);
    return g;
  });
  function heartbeat() {
    if (ctx.state === "closed") return;
    [[0, 60, 0.22, 0.18], [0.25, 50, 0.18, 0.14]].forEach(([t, freq, amp, dur]) => {
      const o = osc(ctx, freq); const g = gain(ctx, 0);
      g.gain.setValueAtTime(vol * amp, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + dur);
      o.connect(g); g.connect(out); o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + dur + 0.05);
    });
    timers.push(setTimeout(heartbeat, 900 + Math.random() * 100));
  }
  timers.push(setTimeout(heartbeat, 1000));
  function swell() {
    if (ctx.state === "closed") return;
    coreNodes.forEach(g => {
      const current = g.gain.value;
      g.gain.linearRampToValueAtTime(current * 2.2, ctx.currentTime + 4);
      g.gain.linearRampToValueAtTime(current, ctx.currentTime + 9);
    });
    timers.push(setTimeout(swell, 18000 + Math.random() * 8000));
  }
  timers.push(setTimeout(swell, 6000));
  function petalNote() {
    if (ctx.state === "closed") return;
    const startFreq = 1320 + Math.random() * 880;
    const o = osc(ctx, startFreq); const g = gain(ctx, 0);
    o.frequency.linearRampToValueAtTime(startFreq * 0.6, ctx.currentTime + 3);
    g.gain.linearRampToValueAtTime(vol * 0.04, ctx.currentTime + 0.2);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 3.5);
    o.connect(g); g.connect(out); o.start(); o.stop(ctx.currentTime + 4);
    timers.push(setTimeout(petalNote, 4000 + Math.random() * 6000));
  }
  petalNote();
  const breath = makeNoise(ctx, 4, "pink");
  const bF = lpf(ctx, 500); const bG = gain(ctx, vol * 0.05);
  breath.connect(bF); bF.connect(bG); bG.connect(out); breath.start();
  return {
    stop: () => { timers.forEach(t => clearTimeout(t)); smoothStop(out, ctx, 3000); },
    setVol: (v) => { out.gain.setTargetAtTime(v * 0.5, ctx.currentTime, 0.15); }
  };
}

function synthFire(vol) {
  const ctx = createAudioContext();
  const out = ctx.createGain();
  out.gain.setValueAtTime(0, ctx.currentTime);
  out.gain.linearRampToValueAtTime(vol * 0.75, ctx.currentTime + 2);
  out.connect(ctx.destination);
  const timers = [];
  const roarNoise = makeNoise(ctx, 4, "brown");
  const roarLpf = lpf(ctx, 280);
  const roarG = gain(ctx, vol * 0.6);
  roarNoise.connect(roarLpf); roarLpf.connect(roarG); roarG.connect(out); roarNoise.start();
  const breathLfo = osc(ctx, 0.9); const breathLfoG = gain(ctx, vol * 0.25);
  breathLfo.connect(breathLfoG); breathLfoG.connect(roarG.gain); breathLfo.start();
  function bassThump() {
    if (ctx.state === "closed") return;
    const o = osc(ctx, 65); const g = gain(ctx, 0);
    o.frequency.setValueAtTime(65, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(25, ctx.currentTime + 0.2);
    g.gain.setValueAtTime(vol * 0.5, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    o.connect(g); g.connect(out); o.start(); o.stop(ctx.currentTime + 0.35);
    timers.push(setTimeout(bassThump, 350 + Math.random() * 300));
  }
  bassThump();
  const crkNoise = makeNoise(ctx, 2, "white");
  const crk1 = bpf(ctx, 1200, 1.5); const crk2 = bpf(ctx, 2800, 1.2);
  const crkG = gain(ctx, vol * 0.2);
  crkNoise.connect(crk1); crk1.connect(crkG);
  crkNoise.connect(crk2); crk2.connect(crkG);
  crkG.connect(out); crkNoise.start();
  function woodPop() {
    if (ctx.state === "closed") return;
    const type = Math.random();
    if (type < 0.5) {
      const n = makeNoise(ctx, 0.1, "white");
      const f = bpf(ctx, 500 + Math.random() * 2000, 0.6);
      const g = gain(ctx, vol * (0.5 + Math.random() * 0.5));
      n.connect(f); f.connect(g); g.connect(out);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06);
      n.start(); n.stop(ctx.currentTime + 0.08);
    } else if (type < 0.75) {
      const o = osc(ctx, 90 + Math.random() * 40); const g = gain(ctx, 0);
      o.frequency.exponentialRampToValueAtTime(20, ctx.currentTime + 0.1);
      g.gain.setValueAtTime(vol * 0.6, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.14);
      o.connect(g); g.connect(out); o.start(); o.stop(ctx.currentTime + 0.16);
    } else {
      const n = makeNoise(ctx, 0.2, "white");
      const f = hpf(ctx, 3000); const g = gain(ctx, vol * 0.15);
      n.connect(f); f.connect(g); g.connect(out);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
      n.start(); n.stop(ctx.currentTime + 0.2);
    }
    timers.push(setTimeout(woodPop, 120 + Math.random() * 600));
  }
  woodPop(); woodPop(); woodPop();
  function intensitySurge() {
    if (ctx.state === "closed") return;
    out.gain.linearRampToValueAtTime(vol * 1.1, ctx.currentTime + 3);
    out.gain.linearRampToValueAtTime(vol * 0.75, ctx.currentTime + 9);
    timers.push(setTimeout(intensitySurge, 20000 + Math.random() * 15000));
  }
  timers.push(setTimeout(intensitySurge, 15000));
  return {
    stop: () => { timers.forEach(t => clearTimeout(t)); smoothStop(out, ctx, 2000); },
    setVol: (v) => { out.gain.setTargetAtTime(v * 0.75, ctx.currentTime, 0.1); }
  };
}

function synthGamma(vol) {
  const ctx = createAudioContext();
  const out = ctx.createGain();
  out.gain.setValueAtTime(0, ctx.currentTime);
  out.gain.linearRampToValueAtTime(vol * 0.65, ctx.currentTime + 2);
  out.connect(ctx.destination);
  const timers = [];
  const noise = makeNoise(ctx, 8, "pink");
  const nLpf = lpf(ctx, 2500); const nG = gain(ctx, vol * 0.35);
  noise.connect(nLpf); nLpf.connect(nG); nG.connect(out); noise.start();
  const bassPattern = [110, 0, 110, 82.5, 0, 110, 0, 82.5];
  let bIdx = 0;
  const bpm = 110;
  const beatMs = (60 / bpm) * 1000;
  function bassBeat() {
    if (ctx.state === "closed") return;
    const freq = bassPattern[bIdx % bassPattern.length];
    if (freq > 0) {
      const o = osc(ctx, freq, "sawtooth"); const g = gain(ctx, 0);
      const f = lpf(ctx, 400);
      o.connect(f); f.connect(g); g.connect(out);
      g.gain.setValueAtTime(vol * 0.45, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + (beatMs * 0.8 / 1000));
      o.start(); o.stop(ctx.currentTime + beatMs / 1000);
    }
    bIdx++;
    timers.push(setTimeout(bassBeat, beatMs));
  }
  bassBeat();
  let kIdx = 0;
  function kickDrum() {
    if (ctx.state === "closed") return;
    if (kIdx % 4 === 0 || kIdx % 4 === 2) {
      const k = osc(ctx, 100); const kG = gain(ctx, 0);
      k.frequency.setValueAtTime(100, ctx.currentTime);
      k.frequency.exponentialRampToValueAtTime(20, ctx.currentTime + 0.15);
      kG.gain.setValueAtTime(vol * 0.55, ctx.currentTime);
      kG.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
      k.connect(kG); kG.connect(out); k.start(); k.stop(ctx.currentTime + 0.22);
    }
    kIdx++;
    timers.push(setTimeout(kickDrum, beatMs));
  }
  timers.push(setTimeout(kickDrum, 100));
  const gmrg = ctx.createChannelMerger(2);
  const gL = osc(ctx, 160); const gR = osc(ctx, 200);
  const glG = gain(ctx, vol * 0.06); const grG = gain(ctx, vol * 0.06);
  gL.connect(glG); glG.connect(gmrg, 0, 0);
  gR.connect(grG); grG.connect(gmrg, 0, 1);
  gmrg.connect(out); gL.start(); gR.start();
  const arpNotes = [220, 277, 330, 440, 330, 277, 220, 165];
  let arpIdx = 0;
  function arpNote() {
    if (ctx.state === "closed") return;
    const freq = arpNotes[arpIdx % arpNotes.length];
    const o = osc(ctx, freq, "triangle"); const g = gain(ctx, 0);
    const f = lpf(ctx, 1500);
    o.connect(f); f.connect(g); g.connect(out);
    g.gain.setValueAtTime(vol * 0.08, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    o.start(); o.stop(ctx.currentTime + 0.4);
    arpIdx++;
    timers.push(setTimeout(arpNote, beatMs * 2));
  }
  timers.push(setTimeout(arpNote, beatMs * 4));
  return {
    stop: () => { timers.forEach(t => clearTimeout(t)); smoothStop(out, ctx, 2000); },
    setVol: (v) => { out.gain.setTargetAtTime(v * 0.65, ctx.currentTime, 0.1); }
  };
}

function synthForest(vol) {
  const ctx = createAudioContext();
  const out = ctx.createGain();
  out.gain.setValueAtTime(0, ctx.currentTime);
  out.gain.linearRampToValueAtTime(vol * 0.65, ctx.currentTime + 4);
  out.connect(ctx.destination);
  const timers = [];
  const sub = osc(ctx, 45); const subG = gain(ctx, vol * 0.18);
  const subLfo = osc(ctx, 0.05); const slG = gain(ctx, 6);
  subLfo.connect(slG); slG.connect(sub.frequency);
  subLfo.start(); sub.connect(subG); subG.connect(out); sub.start();
  [[60, 350, vol * 0.4, 0.05], [350, 1200, vol * 0.28, 0.09], [1200, 4000, vol * 0.14, 0.14]]
    .forEach(([lo, hi, g, rate]) => {
      const n = makeNoise(ctx, 6, "white");
      const lf = lpf(ctx, hi); const hf = hpf(ctx, lo);
      const gn = gain(ctx, g);
      n.connect(lf); lf.connect(hf); hf.connect(gn); gn.connect(out); n.start();
      const lfo = osc(ctx, rate); const lG = gain(ctx, g * 0.7);
      lfo.connect(lG); lG.connect(gn.gain); lfo.start();
    });
  function windGust() {
    if (ctx.state === "closed") return;
    const gustNoise = makeNoise(ctx, 5, "white");
    const gustF = bpf(ctx, 600, 0.4); const gustG = gain(ctx, 0);
    gustNoise.connect(gustF); gustF.connect(gustG); gustG.connect(out);
    gustG.gain.linearRampToValueAtTime(vol * 0.55, ctx.currentTime + 1.5);
    gustG.gain.linearRampToValueAtTime(0, ctx.currentTime + 5);
    gustNoise.start(); gustNoise.stop(ctx.currentTime + 5.5);
    timers.push(setTimeout(windGust, 15000 + Math.random() * 12000));
  }
  timers.push(setTimeout(windGust, 6000));
  const birdSpecies = [
    { f: 2800, warble: 200, dur: 0.12, count: 4, gap: 0.1,  every: 8000  },
    { f: 4200, warble: 400, dur: 0.07, count: 1, gap: 0,    every: 13000 },
    { f: 1600, warble: 60,  dur: 0.4,  count: 2, gap: 0.45, every: 18000 },
  ];
  birdSpecies.forEach(b => {
    function callBird() {
      if (ctx.state === "closed") return;
      if (Math.random() > 0.7) { timers.push(setTimeout(callBird, b.every + Math.random() * 5000)); return; }
      for (let n = 0; n < b.count; n++) {
        const t = n * (b.dur + b.gap);
        const o = osc(ctx, b.f); const g = gain(ctx, 0);
        o.frequency.setValueAtTime(b.f, ctx.currentTime + t);
        o.frequency.linearRampToValueAtTime(b.f + b.warble, ctx.currentTime + t + b.dur * 0.4);
        o.frequency.linearRampToValueAtTime(b.f - b.warble * 0.2, ctx.currentTime + t + b.dur);
        g.gain.linearRampToValueAtTime(vol * 0.13, ctx.currentTime + t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + b.dur + 0.08);
        o.connect(g); g.connect(out); o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + b.dur + 0.1);
      }
      if (Math.random() > 0.5) {
        timers.push(setTimeout(() => {
          if (ctx.state === "closed") return;
          const respFreq = b.f * (1.1 + Math.random() * 0.3);
          const ro = osc(ctx, respFreq); const rg = gain(ctx, 0);
          rg.gain.linearRampToValueAtTime(vol * 0.09, ctx.currentTime + 0.02);
          rg.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + b.dur + 0.1);
          ro.connect(rg); rg.connect(out); ro.start(); ro.stop(ctx.currentTime + b.dur + 0.15);
        }, 2000 + Math.random() * 2000));
      }
      timers.push(setTimeout(callBird, b.every + Math.random() * 6000));
    }
    timers.push(setTimeout(callBird, Math.random() * 4000));
  });
  return {
    stop: () => { timers.forEach(t => clearTimeout(t)); smoothStop(out, ctx, 3000); },
    setVol: (v) => { out.gain.setTargetAtTime(v * 0.65, ctx.currentTime, 0.15); }
  };
}

function synthRain(vol) {
  const ctx = createAudioContext();
  const out = ctx.createGain();
  out.gain.setValueAtTime(0, ctx.currentTime);
  out.gain.linearRampToValueAtTime(vol * 0.72, ctx.currentTime + 3);
  out.connect(ctx.destination);
  const timers = [];
  [[2000, 9000, vol * 0.5], [600, 2500, vol * 0.35], [4000, 12000, vol * 0.2]]
    .forEach(([lo, hi, g]) => {
      const n = makeNoise(ctx, 4, "white");
      const lf = lpf(ctx, hi); const hf = hpf(ctx, lo); const gn = gain(ctx, g);
      n.connect(lf); lf.connect(hf); hf.connect(gn); gn.connect(out); n.start();
    });
  const rainBass = makeNoise(ctx, 6, "brown");
  const rbLpf = lpf(ctx, 150); const rbG = gain(ctx, vol * 0.4);
  rainBass.connect(rbLpf); rbLpf.connect(rbG); rbG.connect(out); rainBass.start();
  const dropTimers = [];
  function scheduleDrop() {
    if (ctx.state === "closed") return;
    const o = osc(ctx, 300 + Math.random() * 2500); const g = gain(ctx, 0);
    o.frequency.exponentialRampToValueAtTime(o.frequency.value * 0.25, ctx.currentTime + 0.05);
    g.gain.setValueAtTime(vol * (0.03 + Math.random() * 0.07), ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06);
    o.connect(g); g.connect(out); o.start(); o.stop(ctx.currentTime + 0.07);
    const t = setTimeout(scheduleDrop, 25 + Math.random() * 70);
    dropTimers.push(t);
  }
  for (let i = 0; i < 6; i++) setTimeout(scheduleDrop, Math.random() * 200);
  function thunder() {
    if (ctx.state === "closed") return;
    if (Math.random() > 0.45) { timers.push(setTimeout(thunder, 18000 + Math.random() * 20000)); return; }
    const lightN = makeNoise(ctx, 0.2, "white");
    const lfH = hpf(ctx, 1000); const lgG = gain(ctx, vol * 0.4);
    lightN.connect(lfH); lfH.connect(lgG); lgG.connect(out);
    lgG.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
    lightN.start(); lightN.stop(ctx.currentTime + 0.2);
    timers.push(setTimeout(() => {
      if (ctx.state === "closed") return;
      const tN = makeNoise(ctx, 5, "brown");
      const tLpf = lpf(ctx, 200); const tG = gain(ctx, 0);
      tN.connect(tLpf); tLpf.connect(tG); tG.connect(out);
      tG.gain.linearRampToValueAtTime(vol * 0.55, ctx.currentTime + 0.8);
      tG.gain.linearRampToValueAtTime(vol * 0.15, ctx.currentTime + 3);
      tG.gain.linearRampToValueAtTime(0, ctx.currentTime + 6);
      tN.start(); tN.stop(ctx.currentTime + 6.5);
    }, 1500));
    timers.push(setTimeout(thunder, 20000 + Math.random() * 25000));
  }
  timers.push(setTimeout(thunder, 12000));
  function rainSurge() {
    if (ctx.state === "closed") return;
    out.gain.linearRampToValueAtTime(vol * 1.05, ctx.currentTime + 4);
    out.gain.linearRampToValueAtTime(vol * 0.72, ctx.currentTime + 12);
    timers.push(setTimeout(rainSurge, 25000 + Math.random() * 20000));
  }
  timers.push(setTimeout(rainSurge, 15000));
  return {
    stop: () => { dropTimers.forEach(t => clearTimeout(t)); timers.forEach(t => clearTimeout(t)); smoothStop(out, ctx, 2500); },
    setVol: (v) => { out.gain.setTargetAtTime(v * 0.72, ctx.currentTime, 0.1); }
  };
}

function synthCosmos(vol) {
  const ctx = createAudioContext();
  const out = ctx.createGain();
  out.gain.setValueAtTime(0, ctx.currentTime);
  out.gain.linearRampToValueAtTime(vol * 0.6, ctx.currentTime + 6);
  out.connect(ctx.destination);
  const timers = [];
  const sub1 = osc(ctx, 28); const sub2 = osc(ctx, 31.5);
  const sG1 = gain(ctx, vol * 0.35); const sG2 = gain(ctx, vol * 0.3);
  const subLfo = osc(ctx, 0.02); const slG = gain(ctx, 5);
  subLfo.connect(slG); slG.connect(sub1.frequency);
  subLfo.start();
  sub1.connect(sG1); sG1.connect(out);
  sub2.connect(sG2); sG2.connect(out);
  sub1.start(); sub2.start();
  const nebulaChords = [
    [55, 82.4, 110, 164.8],
    [49, 73.4, 98,  146.8],
    [43.6, 65.4, 87.3, 130.8],
    [65.4, 98,  130.8, 196],
  ];
  let chordIdx = 0;
  const activeNodes = [];
  function launchNebulaChord() {
    if (ctx.state === "closed") return;
    activeNodes.forEach(g => { try { g.gain.linearRampToValueAtTime(0, ctx.currentTime + 8); } catch(e){} });
    activeNodes.length = 0;
    nebulaChords[chordIdx].forEach(f => {
      [-0.5, 0, 0.5].forEach(detune => {
        const o = osc(ctx, f + detune); const g = gain(ctx, 0);
        const vib = osc(ctx, 0.03 + Math.random() * 0.04);
        const vG = gain(ctx, f * 0.002);
        vib.connect(vG); vG.connect(o.frequency); vib.start();
        o.connect(g); g.connect(out); o.start();
        g.gain.linearRampToValueAtTime(vol * 0.06, ctx.currentTime + 8);
        activeNodes.push(g);
      });
    });
    chordIdx = (chordIdx + 1) % nebulaChords.length;
    timers.push(setTimeout(launchNebulaChord, 22000));
  }
  launchNebulaChord();
  function pulsar() {
    if (ctx.state === "closed") return;
    const o = osc(ctx, 45); const g = gain(ctx, 0);
    o.frequency.setValueAtTime(45, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(15, ctx.currentTime + 0.5);
    g.gain.setValueAtTime(vol * 0.55, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.8);
    o.connect(g); g.connect(out); o.start(); o.stop(ctx.currentTime + 2);
    [2, 3, 4].forEach((harmonic, i) => {
      const ho = osc(ctx, 45 * harmonic); const hg = gain(ctx, 0);
      hg.gain.setValueAtTime(vol * 0.12 / harmonic, ctx.currentTime);
      hg.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 4 - i);
      ho.connect(hg); hg.connect(out); ho.start(); ho.stop(ctx.currentTime + 4.5);
    });
    timers.push(setTimeout(pulsar, 8000 + Math.random() * 6000));
  }
  timers.push(setTimeout(pulsar, 4000));
  const cosmicNoise = makeNoise(ctx, 4, "white");
  const cHpf = hpf(ctx, 8000); const cG = gain(ctx, vol * 0.03);
  cosmicNoise.connect(cHpf); cHpf.connect(cG); cG.connect(out); cosmicNoise.start();
  const windLfo = osc(ctx, 0.04); const wLG = gain(ctx, vol * 0.02);
  windLfo.connect(wLG); wLG.connect(cG.gain); windLfo.start();
  return {
    stop: () => { timers.forEach(t => clearTimeout(t)); smoothStop(out, ctx, 4000); },
    setVol: (v) => { out.gain.setTargetAtTime(v * 0.6, ctx.currentTime, 0.15); }
  };
}

const SYNTH_MAP = {
  binaural: synthBinaural, bowl: synthBowl, brownnoise: synthBrownNoise,
  ocean: synthOcean, love528: synthLove528, fire: synthFire,
  gamma: synthGamma, forest: synthForest, rain: synthRain, cosmos: synthCosmos,
};

const SOUND_LABELS = {
  binaural: "🌙 Binaural Delta", bowl: "🎵 Singing Bowl", brownnoise: "📚 Brown Noise + γ",
  ocean: "🌊 Ocean Waves", love528: "💖 528Hz Love", fire: "🔥 Fire Crackle",
  gamma: "🎯 Gamma Flow", forest: "🌿 Forest Wind", rain: "🌧️ Rain", cosmos: "✨ Space Drone",
};

// ─── SCENE KEYFRAMES ──────────────────────────────────────────────────────────
const SCENE_CSS = `
  @keyframes moonFloat{0%,100%{transform:translateY(0) rotate(-12deg)}50%{transform:translateY(-18px) rotate(-6deg)}}
  @keyframes starTwinkle{0%,100%{opacity:0.15;transform:scale(1)}50%{opacity:1;transform:scale(1.5)}}
  @keyframes cloudDrift{0%{transform:translateX(-200px);opacity:0}10%{opacity:0.2}90%{opacity:0.08}100%{transform:translateX(110vw);opacity:0}}
  @keyframes zzRise{0%{opacity:0;transform:translateY(0) rotate(0deg)}15%{opacity:0.8}80%{opacity:0.2}100%{opacity:0;transform:translateY(-90px) rotate(20deg)}}
  @keyframes mandalaRot{from{transform:translate(-50%,-50%) rotate(0deg)}to{transform:translate(-50%,-50%) rotate(360deg)}}
  @keyframes mandalaRotRev{from{transform:translate(-50%,-50%) rotate(0deg)}to{transform:translate(-50%,-50%) rotate(-360deg)}}
  @keyframes rippleOut{0%{transform:translate(-50%,-50%) scale(0.2);opacity:0.7}100%{transform:translate(-50%,-50%) scale(3.5);opacity:0}}
  @keyframes particleAsc{0%{opacity:0;transform:translateY(0)}15%{opacity:0.8}85%{opacity:0.1}100%{opacity:0;transform:translateY(-95vh)}}
  @keyframes lampPulse{0%,100%{opacity:0.4}50%{opacity:0.85}}
  @keyframes bookBob{0%,100%{transform:translateY(0) rotate(var(--br,-4deg))}50%{transform:translateY(-12px) rotate(var(--br2,4deg))}}
  @keyframes dustFloat{0%{opacity:0;transform:translate(0,0)}20%{opacity:0.6}80%{opacity:0.2}100%{opacity:0;transform:translate(var(--dx,20px),var(--dy,-60px))}}
  @keyframes waveShift1{0%,100%{transform:translateX(0) scaleY(1)}50%{transform:translateX(-35px) scaleY(1.15)}}
  @keyframes waveShift2{0%,100%{transform:translateX(0)}50%{transform:translateX(28px) scaleY(0.9)}}
  @keyframes waveShift3{0%,100%{transform:translateX(0)}50%{transform:translateX(-20px)}}
  @keyframes bubbleRise{0%{opacity:0;transform:translateY(0)}10%{opacity:0.6}90%{opacity:0.1}100%{opacity:0;transform:translateY(-110px)}}
  @keyframes heartAscend{0%{opacity:0;transform:translateY(0) scale(0.5) rotate(-12deg)}12%{opacity:0.9;transform:scale(1.1) rotate(5deg)}88%{opacity:0.15}100%{opacity:0;transform:translateY(-98vh) scale(0.6) rotate(-5deg)}}
  @keyframes petalDescend{0%{opacity:0;transform:translateY(-20px) rotate(0deg)}12%{opacity:0.85}88%{opacity:0.1}100%{opacity:0;transform:translateY(110vh) rotate(450deg)}}
  @keyframes heartBeat{0%,100%{transform:scale(1)}14%{transform:scale(1.22)}28%{transform:scale(1)}42%{transform:scale(1.12)}56%{transform:scale(1)}}
  @keyframes flameA{0%,100%{transform:scaleX(1) scaleY(1) translateX(0);opacity:0.95}25%{transform:scaleX(1.14) scaleY(1.08) translateX(-4px);opacity:1}75%{transform:scaleX(0.9) scaleY(1.14) translateX(4px);opacity:0.88}}
  @keyframes flameB{0%,100%{transform:scaleX(1) scaleY(1);opacity:0.75}33%{transform:scaleX(0.85) scaleY(1.12) translateX(3px);opacity:0.9}66%{transform:scaleX(1.15) scaleY(0.92) translateX(-3px);opacity:0.65}}
  @keyframes emberFly{0%{opacity:0.95;transform:translate(0,0) scale(1)}100%{opacity:0;transform:translate(var(--ex,20px),var(--ey,-80px)) scale(0.15)}}
  @keyframes gridGlow{0%,100%{opacity:0.04}50%{opacity:0.12}}
  @keyframes laserBeam{0%{transform:translateX(-110%) skewX(-20deg);opacity:0.6}100%{transform:translateX(120vw) skewX(-20deg);opacity:0.05}}
  @keyframes targetPulse{0%,100%{transform:translate(-50%,-50%) scale(1);opacity:0.45}50%{transform:translate(-50%,-50%) scale(1.14);opacity:1}}
  @keyframes dataFade{0%,100%{opacity:0;transform:translateY(-10px)}50%{opacity:0.65;transform:translateY(0)}}
  @keyframes treeSway{0%,100%{transform:rotate(var(--ta,-1deg))}50%{transform:rotate(var(--tb,1.8deg))}}
  @keyframes fireflyGlow{0%{opacity:0;transform:translate(0,0)}20%{opacity:1}50%{transform:translate(var(--fx,18px),var(--fy,-24px));opacity:0.5}80%{opacity:0.75}100%{opacity:0;transform:translate(var(--fx2,-12px),var(--fy2,14px))}}
  @keyframes leafSpin{0%{opacity:0.85;transform:translateY(0) rotate(0deg)}100%{opacity:0;transform:translateY(100vh) rotate(400deg)}}
  @keyframes rainFall{0%{opacity:0;transform:translateY(-20px)}8%{opacity:0.7}92%{opacity:0.4}100%{opacity:0;transform:translateY(112vh)}}
  @keyframes puddle{0%{transform:translate(-50%,-50%) scale(0.1);opacity:0.8}100%{transform:translate(-50%,-50%) scale(1.2);opacity:0}}
  @keyframes lightningFlash{0%,93%,100%{opacity:0}94%,97%{opacity:0.22}}
  @keyframes nebulaBreath{0%,100%{opacity:0.12;transform:scale(1) rotate(0deg)}50%{opacity:0.25;transform:scale(1.08) rotate(4deg)}}
  @keyframes starBlink{0%,100%{opacity:0.1}50%{opacity:1}}
  @keyframes shootStar{0%{transform:translate(0,0);opacity:0}5%{opacity:1}40%{transform:translate(260px,110px);opacity:0}100%{opacity:0}}
  @keyframes galaxyTurn{from{transform:translate(-50%,-50%) rotate(0deg)}to{transform:translate(-50%,-50%) rotate(360deg)}}
  @keyframes timerGlow{0%,100%{opacity:0.4}50%{opacity:0.9}}
  @keyframes fadeInUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
  @keyframes panelReveal{from{opacity:0;transform:translateY(30px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}}
  @keyframes pulseRing{0%{transform:scale(1);opacity:0.6}100%{transform:scale(1.8);opacity:0}}
  @keyframes breathingPulse{0%,100%{transform:scale(1);opacity:0.7}50%{transform:scale(1.06);opacity:1}}
  @keyframes slideUpSheet{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}}
  @keyframes backdropIn{from{opacity:0}to{opacity:1}}
  @keyframes musicBarA{0%,100%{height:4px}50%{height:18px}}
  @keyframes musicBarB{0%,100%{height:12px}50%{height:4px}}
  @keyframes musicBarC{0%,100%{height:8px}50%{height:20px}}
`;

// ─── SCENE COMPONENTS ────────────────────────────────────────────────────────
function SleepScene({ accent, glow }) {
  const stars = Array.from({length:90},(_,i)=>({left:`${(i*37+13)%100}%`,top:`${(i*53+7)%85}%`,size:i%8===0?3.5:i%4===0?2.5:1.5,delay:`${(i*0.22)%5}s`,dur:`${2+i%4}s`}));
  return (
    <div style={{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none"}}>
      <div style={{position:"absolute",top:"8%",right:"12%",width:110,height:110,borderRadius:"50%",background:`radial-gradient(circle at 35% 35%, #fff8e1, ${accent}dd)`,boxShadow:`0 0 70px ${glow}70,0 0 140px ${glow}30`,animation:"moonFloat 7s ease-in-out infinite"}}>
        {[[22,28,11],[52,42,7],[32,62,9]].map(([x,y,s],i)=>(<div key={i} style={{position:"absolute",left:`${x}%`,top:`${y}%`,width:s,height:s,borderRadius:"50%",background:"rgba(0,0,0,0.14)"}}/>))}
      </div>
      {stars.map((s,i)=>(<div key={i} style={{position:"absolute",left:s.left,top:s.top,width:s.size,height:s.size,borderRadius:"50%",background:"#fff",boxShadow:s.size>2?`0 0 ${s.size*4}px rgba(255,255,255,0.95),0 0 ${s.size*8}px ${accent}60`:"none",animation:`starTwinkle ${s.dur} ${s.delay} ease-in-out infinite`}}/>))}
      {[0,1,2,3].map(i=>(<div key={i} style={{position:"absolute",top:`${18+i*16}%`,width:`${200+i*80}px`,height:`${40+i*14}px`,borderRadius:60,background:`radial-gradient(ellipse,${accent}20 0%,transparent 70%)`,animation:`cloudDrift ${22+i*8}s ${i*7}s linear infinite`,filter:"blur(2px)"}}/>))}
      {["z","z","Z","Zz","zzz","z"].map((z,i)=>(<div key={i} style={{position:"absolute",bottom:`${15+i*8}%`,left:`${35+i*10}%`,color:`${accent}aa`,fontSize:`${11+i*4}px`,fontWeight:800,fontFamily:"serif",animation:`zzRise ${3.5+i*0.6}s ${i*1.2}s ease-out infinite`}}>{z}</div>))}
    </div>
  );
}
function MeditateScene({ accent, glow }) {
  const rings=[90,145,200,260,325,390];
  return (
    <div style={{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none"}}>
      <div style={{position:"absolute",inset:0,background:`radial-gradient(ellipse at 50% 50%, ${glow}12 0%, transparent 65%)`}}/>
      {rings.map((size,i)=>(<div key={i} style={{position:"absolute",top:"50%",left:"50%",width:size,height:size,border:`1px solid ${accent}${["50","35","28","20","15","10"][i]}`,borderRadius:"50%",animation:`${i%2===0?"mandalaRot":"mandalaRotRev"} ${20+i*14}s linear infinite`}}/>))}
      {Array.from({length:12},(_,i)=>i).map(i=>(<div key={i} style={{position:"absolute",top:"50%",left:"50%",width:2,height:80,background:`linear-gradient(to bottom,${accent}70,transparent)`,transformOrigin:"top center",transform:`translate(-50%,0) rotate(${i*30}deg)`,borderRadius:2,animation:"mandalaRot 15s linear infinite"}}/>))}
      {[0,1,2,3,4].map(i=>(<div key={i} style={{position:"absolute",top:"50%",left:"50%",width:70,height:70,borderRadius:"50%",border:`1.5px solid ${accent}60`,animation:`rippleOut 6s ${i*1.2}s ease-out infinite`}}/>))}
      {Array.from({length:30},(_,i)=>({left:`${(i*17+5)%90+5}%`,delay:`${(i*0.3)%6}s`,dur:`${6+i%5}s`,size:i%4+2})).map((p,i)=>(<div key={i} style={{position:"absolute",bottom:0,left:p.left,width:p.size,height:p.size,borderRadius:"50%",background:accent,opacity:0,animation:`particleAsc ${p.dur} ${p.delay} ease-out infinite`}}/>))}
      <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:50,height:50,borderRadius:"50%",background:`radial-gradient(circle,${glow},transparent)`,boxShadow:`0 0 40px ${glow},0 0 80px ${glow}50`,animation:"breathingPulse 3s ease-in-out infinite"}}/>
    </div>
  );
}
function StudyScene({ accent }) {
  const books=[{x:8,y:38,w:36,h:52,rot:-8,col:"#8B4513"},{x:13,y:40,w:30,h:48,rot:4,col:"#1565C0"},{x:18,y:39,w:34,h:50,rot:-3,col:"#2E7D32"},{x:72,y:34,w:38,h:54,rot:6,col:"#6A1B9A"},{x:77,y:36,w:32,h:48,rot:-5,col:"#BF360C"},{x:82,y:35,w:36,h:50,rot:3,col:"#00695C"}];
  const symbols=["∑","π","∞","√","∂","∫","≡","∀","∃","Δ","λ","Ω","φ"];
  return (
    <div style={{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none"}}>
      <div style={{position:"absolute",bottom:"22%",right:"10%",width:300,height:300,borderRadius:"50%",background:`radial-gradient(ellipse,${accent}28 0%,transparent 70%)`,animation:"lampPulse 4s ease-in-out infinite"}}/>
      {books.map((b,i)=>(<div key={i} style={{"--br":`${b.rot}deg`,"--br2":`${-b.rot}deg`,position:"absolute",left:`${b.x}%`,top:`${b.y}%`,width:b.w,height:b.h,background:b.col,borderRadius:"2px 5px 5px 2px",transform:`rotate(${b.rot}deg)`,animation:`bookBob ${3.5+i*0.4}s ${i*0.5}s ease-in-out infinite`,opacity:0.9}}><div style={{position:"absolute",left:5,top:4,right:4,bottom:4,borderLeft:"3px solid rgba(255,255,255,0.2)"}}/></div>))}
      {symbols.map((sym,i)=>(<div key={i} style={{"--dx":`${(i%5-2)*30}px`,"--dy":`${-(40+i%4*22)}px`,position:"absolute",left:`${(i*9+6)%86}%`,top:`${(i*11+8)%68}%`,color:`${accent}60`,fontSize:18,fontFamily:"Georgia,serif",animation:`dustFloat ${6+i}s ${i*0.7}s ease-in-out infinite`}}>{sym}</div>))}
    </div>
  );
}
function OceanScene({ accent, glow }) {
  return (
    <div style={{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none"}}>
      <div style={{position:"absolute",inset:0,background:`radial-gradient(ellipse at 50% 70%, ${glow}14 0%, transparent 60%)`}}/>
      {[{b:"8%",h:120,col:`${accent}38`,anim:"waveShift1 9s ease-in-out infinite"},{b:"4%",h:90,col:`${accent}28`,anim:"waveShift2 12s ease-in-out infinite"},{b:"0%",h:65,col:`${accent}18`,anim:"waveShift3 15s ease-in-out infinite"}].map((w,i)=>(<div key={i} style={{position:"absolute",bottom:w.b,left:"-6%",right:"-6%",height:w.h,background:w.col,borderRadius:"70% 70% 0 0 / 40% 40% 0 0",animation:w.anim}}/>))}
      {Array.from({length:20},(_,i)=>({left:`${(i*6+3)%92}%`,bottom:`${5+i%5*3}%`,size:3+i%6,delay:`${i*0.38}s`,dur:`${3.5+i%4}s`})).map((b,i)=>(<div key={i} style={{position:"absolute",left:b.left,bottom:b.bottom,width:b.size,height:b.size,borderRadius:"50%",border:`1.5px solid ${accent}90`,opacity:0,animation:`bubbleRise ${b.dur} ${b.delay} ease-out infinite`}}/>))}
    </div>
  );
}
function LoveScene({ accent, glow }) {
  const hearts=Array.from({length:22},(_,i)=>({left:`${(i*5+2)%96}%`,emoji:["💖","💗","💓","💕","❤️","🌸","💝"][i%7],size:13+i%5*7,delay:`${(i*0.5)%9}s`,dur:`${7+i%5}s`}));
  const petals=Array.from({length:15},(_,i)=>({left:`${(i*7+2)%97}%`,size:10+i%3*5,delay:`${(i*0.6)%7}s`,dur:`${9+i%6}s`}));
  return (
    <div style={{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none"}}>
      <div style={{position:"absolute",top:"30%",left:"50%",transform:"translate(-50%,-50%)",fontSize:90,opacity:0.08,animation:"heartBeat 1.6s ease-in-out infinite",filter:`drop-shadow(0 0 35px ${glow})`}}>💖</div>
      {hearts.map((h,i)=>(<div key={i} style={{position:"absolute",bottom:"-8%",left:h.left,fontSize:h.size,opacity:0,animation:`heartAscend ${h.dur} ${h.delay} ease-out infinite`}}>{h.emoji}</div>))}
      {petals.map((p,i)=>(<div key={i} style={{position:"absolute",top:"-8%",left:p.left,fontSize:p.size,opacity:0,animation:`petalDescend ${p.dur} ${p.delay} linear infinite`}}>🌸</div>))}
    </div>
  );
}
function FireScene({ accent }) {
  const embers=Array.from({length:30},(_,i)=>({x:`${36+i%7*4}%`,bottom:`${5+i%6*2}%`,ex:`${(i%6-3)*35}px`,ey:`${-(45+i%5*20)}px`,delay:`${i*0.22}s`,dur:`${1.5+i%3*0.6}s`}));
  return (
    <div style={{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none"}}>
      <div style={{position:"absolute",bottom:"-5%",left:"50%",transform:"translateX(-50%)",width:280,height:420,background:`radial-gradient(ellipse at 50% 90%,${accent} 0%,${accent}75 22%,${accent}30 50%,transparent 76%)`,borderRadius:"60% 60% 0 0",animation:"flameA 0.5s ease-in-out infinite",filter:"blur(18px)"}}/>
      <div style={{position:"absolute",bottom:"-5%",left:"50%",transform:"translateX(-50%)",width:160,height:280,background:`radial-gradient(ellipse at 50% 90%,#fff9e6 0%,#FFD700 16%,${accent}90 42%,transparent 70%)`,borderRadius:"60% 60% 0 0",animation:"flameB 0.35s ease-in-out infinite",filter:"blur(7px)"}}/>
      {embers.map((e,i)=>(<div key={i} style={{"--ex":e.ex,"--ey":e.ey,position:"absolute",left:e.x,bottom:e.bottom,width:3,height:3,borderRadius:"50%",background:"#FFD700",animation:`emberFly ${e.dur} ${e.delay} ease-out infinite`}}/>))}
      <div style={{position:"absolute",inset:0,background:`radial-gradient(ellipse at 50% 100%, ${accent}20 0%, transparent 55%)`}}/>
    </div>
  );
}
function FlowScene({ accent, glow }) {
  const dataItems=Array.from({length:12},(_,i)=>({x:`${(i*9+4)%88}%`,y:`${(i*11+4)%82}%`,val:String(Math.floor((i*173+57)%1000)).padStart(3,"0"),delay:`${i*0.38}s`}));
  return (
    <div style={{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none"}}>
      <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",animation:"gridGlow 4s ease-in-out infinite"}} xmlns="http://www.w3.org/2000/svg">
        <defs><pattern id="focusgrid" width="50" height="50" patternUnits="userSpaceOnUse"><path d="M 50 0 L 0 0 0 50" fill="none" stroke={accent} strokeWidth="0.5" opacity="0.5"/></pattern></defs>
        <rect width="100%" height="100%" fill="url(#focusgrid)"/>
      </svg>
      {[130,90,55,28].map((size,i)=>(<div key={i} style={{position:"absolute",top:"50%",left:"50%",width:size,height:size,borderRadius:"50%",border:`1.5px solid ${accent}${["70","50","38","60"][i]}`,animation:`targetPulse ${2.2+i*0.45}s ${i*0.28}s ease-in-out infinite`}}/>))}
      <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:10,height:10,borderRadius:"50%",background:glow,boxShadow:`0 0 18px ${glow},0 0 35px ${glow}80`}}/>
      {[0,1,2].map(i=>(<div key={i} style={{position:"absolute",top:`${24+i*24}%`,left:0,width:"100%",height:2,background:`linear-gradient(90deg,transparent,${accent}70,transparent)`,animation:`laserBeam ${5+i*2.5}s ${i*2}s linear infinite`}}/>))}
      {dataItems.map((d,i)=>(<div key={i} style={{position:"absolute",left:d.x,top:d.y,color:`${accent}60`,fontSize:11,fontFamily:"monospace",fontWeight:700,animation:`dataFade 4s ${d.delay} ease-in-out infinite`}}>{d.val}</div>))}
    </div>
  );
}
function ForestScene({ accent }) {
  const trees=[{x:0,h:70,w:20,col:"#0a2010",ta:"-1deg",tb:"2.2deg",delay:"0s",dur:"6s"},{x:10,h:82,w:15,col:"#0d2614",ta:"1.3deg",tb:"-2.5deg",delay:"0.4s",dur:"7s"},{x:20,h:88,w:22,col:"#0a1e0c",ta:"-0.8deg",tb:"1.8deg",delay:"0.9s",dur:"8s"},{x:64,h:78,w:17,col:"#0d2614",ta:"1.1deg",tb:"-2deg",delay:"1.1s",dur:"6.5s"},{x:75,h:90,w:24,col:"#0a1e0c",ta:"-1.3deg",tb:"2.2deg",delay:"0.3s",dur:"7.5s"},{x:87,h:72,w:16,col:"#0a2010",ta:"0.9deg",tb:"-1.7deg",delay:"0.7s",dur:"8.5s"}];
  const fireflies=Array.from({length:24},(_,i)=>({x:`${(i*6+3)%86}%`,y:`${(i*9+10)%68}%`,fx:`${(i%5-2)*25}px`,fy:`${-(i%4)*18}px`,fx2:`${(i%3-1)*16}px`,fy2:`${(i%5)*14}px`,delay:`${(i*0.48)%6}s`,dur:`${4.5+i%4}s`}));
  return (
    <div style={{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none"}}>
      {trees.map((tree,i)=>(<div key={i} style={{"--ta":tree.ta,"--tb":tree.tb,position:"absolute",bottom:0,left:`${tree.x}%`,width:`${tree.w}%`,height:`${tree.h}%`,background:tree.col,clipPath:"polygon(25% 100%, 50% 0%, 75% 100%)",animation:`treeSway ${tree.dur} ${tree.delay} ease-in-out infinite`,transformOrigin:"bottom center",opacity:0.88}}/>))}
      <div style={{position:"absolute",bottom:0,left:"-5%",right:"-5%",height:"16%",background:"#040a04",borderRadius:"50% 50% 0 0",opacity:0.95}}/>
      {fireflies.map((f,i)=>(<div key={i} style={{"--fx":f.fx,"--fy":f.fy,"--fx2":f.fx2,"--fy2":f.fy2,position:"absolute",left:f.x,top:f.y,width:3.5,height:3.5,borderRadius:"50%",background:accent,boxShadow:`0 0 9px 3px ${accent}95`,opacity:0,animation:`fireflyGlow ${f.dur} ${f.delay} ease-in-out infinite`}}/>))}
      {["🍃","🍂","🌿","🍀","🌾"].map((l,i)=>(<div key={i} style={{position:"absolute",top:"-8%",left:`${12+i*19}%`,fontSize:12,opacity:0,animation:`leafSpin ${10+i*2.5}s ${i*2.8}s linear infinite`}}>{l}</div>))}
    </div>
  );
}
function RainScene({ accent }) {
  const drops=Array.from({length:80},(_,i)=>({left:`${(i*1.3+0.2)%100}%`,h:16+i%6*10,delay:`${(i*0.032)%2.2}s`,dur:`${0.5+i%4*0.12}s`,w:i%4===0?2:1,opacity:0.3+i%4*0.12}));
  const ripples=Array.from({length:12},(_,i)=>({left:`${(i*10+5)%88}%`,top:`${75+i%4*5}%`,w:24+i*9,delay:`${i*0.42}s`}));
  return (
    <div style={{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none"}}>
      {drops.map((r,i)=>(<div key={i} style={{position:"absolute",top:0,left:r.left,width:r.w,height:r.h,background:`linear-gradient(to bottom,transparent,${accent}98)`,borderRadius:1,opacity:r.opacity,animation:`rainFall ${r.dur} ${r.delay} linear infinite`}}/>))}
      {ripples.map((p,i)=>(<div key={i} style={{position:"absolute",left:p.left,top:p.top,width:p.w,height:p.w/3.5,border:`1px solid ${accent}60`,borderRadius:"50%",animation:`puddle 1.8s ${p.delay} ease-out infinite`}}/>))}
      <div style={{position:"absolute",inset:0,background:"rgba(180,210,255,0.06)",animation:"lightningFlash 18s ease-in-out infinite"}}/>
    </div>
  );
}
function CosmosScene({ accent }) {
  const stars=Array.from({length:110},(_,i)=>({left:`${(i*37+7)%100}%`,top:`${(i*53+3)%98}%`,size:i%10===0?3.5:i%4===0?2.5:1.5,delay:`${(i*0.15)%6}s`,dur:`${2.5+i%5}s`}));
  return (
    <div style={{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none"}}>
      {[{x:15,y:25,w:420,h:240,col:"rgba(197,184,255,0.16)"},{x:55,y:45,w:320,h:190,col:"rgba(100,150,255,0.10)"},{x:5,y:55,w:280,h:175,col:"rgba(255,150,200,0.08)"},{x:68,y:12,w:240,h:160,col:"rgba(150,200,255,0.09)"}].map((n,i)=>(<div key={i} style={{position:"absolute",left:`${n.x}%`,top:`${n.y}%`,width:n.w,height:n.h,background:n.col,borderRadius:"50%",filter:"blur(40px)",animation:`nebulaBreath ${9+i*3.5}s ${i*2.2}s ease-in-out infinite`}}/>))}
      {stars.map((s,i)=>(<div key={i} style={{position:"absolute",left:s.left,top:s.top,width:s.size,height:s.size,borderRadius:"50%",background:"#fff",boxShadow:s.size>2?`0 0 ${s.size*4}px ${accent}99,0 0 ${s.size*8}px rgba(255,255,255,0.4)`:"none",animation:`starBlink ${s.dur} ${s.delay} ease-in-out infinite`}}/>))}
      {[0,1,2,3].map(i=>(<div key={i} style={{position:"absolute",top:`${6+i*14}%`,left:`${-8+i*6}%`,width:100,height:2,background:`linear-gradient(90deg,transparent,${accent}90,white,transparent)`,borderRadius:1,opacity:0,animation:`shootStar ${10+i*5}s ${i*6}s ease-in infinite`}}/>))}
      <div style={{position:"absolute",top:"50%",left:"50%",width:280,height:280,opacity:0.08,animation:"galaxyTurn 40s linear infinite"}}>
        {Array.from({length:6},(_,i)=>(<div key={i} style={{position:"absolute",top:"50%",left:"50%",transform:`translate(-50%,-50%) rotate(${i*30}deg)`,width:"100%",height:2,background:`linear-gradient(90deg,transparent,${accent},transparent)`,borderRadius:1}}/>))}
      </div>
    </div>
  );
}
function ThemeScene({ theme }) {
  const p={accent:theme.accent,glow:theme.accentGlow};
  const map={sleep:<SleepScene {...p}/>,meditate:<MeditateScene {...p}/>,study:<StudyScene {...p}/>,ocean:<OceanScene {...p}/>,love:<LoveScene {...p}/>,fire:<FireScene {...p}/>,flow:<FlowScene {...p}/>,forest:<ForestScene {...p}/>,rain:<RainScene {...p}/>,cosmos:<CosmosScene {...p}/>};
  return map[theme.animationType]||null;
}

// ─── Circular Timer ────────────────────────────────────────────────────────────
function CircularTimer({ timeLeft, totalSeconds, accent, accentGlow, size, textColor, mutedColor }) {
  const r=(size/2)-14; const circ=2*Math.PI*r;
  const offset=circ*(1-(totalSeconds>0?timeLeft/totalSeconds:1));
  const mins=String(Math.floor(timeLeft/60)).padStart(2,"0");
  const secs=String(timeLeft%60).padStart(2,"0");
  return (
    <div style={{position:"relative",width:size,height:size}}>
      <div style={{position:"absolute",inset:-8,borderRadius:"50%",border:`2px solid ${accent}`,opacity:0.15,animation:"pulseRing 3s ease-out infinite"}}/>
      <div style={{position:"absolute",inset:-8,borderRadius:"50%",border:`2px solid ${accent}`,opacity:0.1,animation:"pulseRing 3s 1.5s ease-out infinite"}}/>
      <div style={{position:"absolute",inset:-20,borderRadius:"50%",boxShadow:`0 0 80px ${accentGlow}50,0 0 150px ${accentGlow}20`,animation:"timerGlow 3s ease-in-out infinite",pointerEvents:"none"}}/>
      <svg width={size} height={size} style={{transform:"rotate(-90deg)",position:"relative",zIndex:1}}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8"/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={accent} strokeWidth="8" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset} style={{transition:"stroke-dashoffset 0.9s linear",filter:`drop-shadow(0 0 12px ${accentGlow})`}}/>
      </svg>
      <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",zIndex:2}}>
        <div style={{fontFamily:"'DM Serif Display',serif",fontSize:size>240?"3.6rem":"2.8rem",color:textColor,letterSpacing:"-0.03em",lineHeight:1,textShadow:`0 0 40px ${accentGlow}90,0 2px 8px rgba(0,0,0,0.5)`}}>{mins}:{secs}</div>
        <div style={{fontSize:"0.65rem",color:mutedColor,marginTop:6,letterSpacing:"0.18em",textTransform:"uppercase",fontWeight:600}}>{timeLeft===0?"✓ complete":"remaining"}</div>
      </div>
    </div>
  );
}

function VolumeSlider({ value, onChange, accent }) {
  const pct=value*100;
  return (
    <div style={{display:"flex",alignItems:"center",gap:12,width:"100%"}}>
      <span style={{fontSize:"0.9rem",opacity:0.6}}>🔈</span>
      <div style={{flex:1,position:"relative",height:6}}>
        <div style={{position:"absolute",inset:0,borderRadius:3,background:`linear-gradient(90deg,#FF6B6B 0%,#FFD93D 30%,#6BCB77 65%,${accent} 100%)`,opacity:0.35}}/>
        <div style={{position:"absolute",top:0,left:0,bottom:0,width:`${pct}%`,borderRadius:3,background:`linear-gradient(90deg,#FF6B6B 0%,#FFD93D 30%,#6BCB77 65%,${accent} 100%)`,transition:"width 0.05s"}}/>
        <input type="range" min={0} max={1} step={0.02} value={value} onChange={e=>onChange(parseFloat(e.target.value))} style={{position:"absolute",inset:0,width:"100%",height:"100%",opacity:0,cursor:"pointer",zIndex:2}}/>
        <div style={{position:"absolute",top:"50%",left:`${pct}%`,transform:"translate(-50%,-50%)",width:16,height:16,borderRadius:"50%",background:"#fff",boxShadow:`0 0 8px ${accent}80,0 2px 6px rgba(0,0,0,0.4)`,pointerEvents:"none",transition:"left 0.05s",zIndex:3}}/>
      </div>
      <span style={{fontSize:"0.9rem",opacity:0.6}}>🔊</span>
    </div>
  );
}

function MusicBars({ accent, playing }) {
  return (
    <div style={{display:"flex",gap:3,alignItems:"flex-end",height:18,flexShrink:0}}>
      {["musicBarA","musicBarB","musicBarC"].map((anim,i)=>(
        <div key={i} style={{width:3,borderRadius:2,background:accent,height:playing?undefined:5,animation:playing?`${anim} 0.8s ${i*0.15}s ease-in-out infinite`:"none",transition:"height 0.3s"}}/>
      ))}
    </div>
  );
}

// ─── LOCAL MUSIC PLAYER SHEET ─────────────────────────────────────────────────
function LocalMusicSheet({ onClose, theme, onPlayStateChange }) {
  const [tracks, setTracks]         = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [isPlaying, setIsPlaying]   = useState(false);
  const [shuffle, setShuffle]       = useState(false);
  const [progress, setProgress]     = useState(0);
  const [duration, setDuration]     = useState(0);
  const [volume, setVolume]         = useState(0.8);
  const [dragging, setDragging]     = useState(false);
  const audioRef = useRef(null);
  const blobUrls = useRef([]);
  const th = theme;

  const glass = { background:th.cardBg, backdropFilter:"blur(24px)", WebkitBackdropFilter:"blur(24px)", border:`1px solid ${th.borderColor}`, borderRadius:20 };

  useEffect(() => () => blobUrls.current.forEach(u=>URL.revokeObjectURL(u)), []);
  useEffect(() => { onPlayStateChange(isPlaying); }, [isPlaying]);
  useEffect(() => { if (audioRef.current) audioRef.current.volume = volume; }, [volume]);
  useEffect(() => {
    if (!audioRef.current || tracks.length === 0) return;
    audioRef.current.src = tracks[currentIdx]?.url || "";
    if (isPlaying) audioRef.current.play().catch(()=>{});
  }, [currentIdx, tracks]);

  function handleFiles(files) {
    const audio = Array.from(files).filter(f=>f.type.startsWith("audio/"));
    if (!audio.length) return;
    const newTracks = audio.map(f => { const url=URL.createObjectURL(f); blobUrls.current.push(url); return { name:f.name.replace(/\.[^/.]+$/,""), url }; });
    setTracks(prev=>[...prev,...newTracks]);
  }

  function handleDrop(e) { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }

  function playTrack(idx) {
    setCurrentIdx(idx); setIsPlaying(true);
    setTimeout(()=>audioRef.current?.play().catch(()=>{}),50);
  }

  function togglePlay() {
    if (!tracks.length) return;
    if (isPlaying) { audioRef.current?.pause(); setIsPlaying(false); }
    else { audioRef.current?.play().catch(()=>{}); setIsPlaying(true); }
  }

  function prev() { playTrack(shuffle?Math.floor(Math.random()*tracks.length):(currentIdx-1+tracks.length)%tracks.length); }
  function next() { playTrack(shuffle?Math.floor(Math.random()*tracks.length):(currentIdx+1)%tracks.length); }

  function handleTimeUpdate() {
    if (!audioRef.current) return;
    setProgress(audioRef.current.currentTime);
    setDuration(audioRef.current.duration||0);
  }

  function seek(e) {
    if (!audioRef.current||!duration) return;
    const rect=e.currentTarget.getBoundingClientRect();
    const ratio=Math.max(0,Math.min(1,(e.clientX-rect.left)/rect.width));
    audioRef.current.currentTime=ratio*duration; setProgress(ratio*duration);
  }

  function removeTrack(i, e) {
    e.stopPropagation();
    const newTracks=[...tracks]; newTracks.splice(i,1); setTracks(newTracks);
    if (i===currentIdx&&newTracks.length>0) setCurrentIdx(Math.min(i,newTracks.length-1));
    if (newTracks.length===0) { setIsPlaying(false); }
  }

  function fmt(s) { if(!s||isNaN(s)) return "0:00"; return `${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,"0")}`; }

  const ct=tracks[currentIdx];
  const pct=duration>0?(progress/duration)*100:0;

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position:"fixed", inset:0, zIndex:9998,
          background:"rgba(0,0,0,0.55)", backdropFilter:"blur(4px)",
          animation:"backdropIn 0.3s ease",
        }}
      />
      <div style={{
        position:"fixed",
        // 🔥 CSS Vars force the sheet beneath the PWA top bar exactly
        top: "calc(var(--topbar-h, 48px) + var(--safe-top, 0px))",
        bottom: 0, left: 0, right: 0,
        zIndex: 9999, display: "flex", flexDirection: "column",
        background: "linear-gradient(180deg,rgba(0,0,0,0.88) 0%,rgba(0,0,0,0.96) 100%)",
        backdropFilter: "blur(30px)", WebkitBackdropFilter: "blur(30px)",
        borderTop: `1.5px solid ${th.accent}45`,
        borderRadius: "24px 24px 0 0",
        animation: "slideUpSheet 0.42s cubic-bezier(0.22,1,0.36,1)",
        overflow: "hidden",
      }}>
        <div style={{display:"flex",justifyContent:"center",padding:"12px 0 0",flexShrink:0}}>
          <div style={{width:44,height:5,borderRadius:3,background:`${th.accent}50`}}/>
        </div>

        <div style={{
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"10px 24px 8px", flexShrink: 0,
        }}>
          <div>
            <div style={{fontWeight:800,color:th.textColor,fontSize:"1rem",letterSpacing:"-0.02em"}}>🎵 My Music</div>
            <div style={{color:th.mutedColor,fontSize:"0.68rem",marginTop:2}}>{tracks.length} track{tracks.length!==1?"s":""} · plays locally, never uploaded</div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <button
              onClick={()=>setShuffle(s=>!s)}
              style={{background:shuffle?`${th.accent}28`:"rgba(255,255,255,0.07)",border:`1.5px solid ${shuffle?th.accent:"rgba(255,255,255,0.12)"}`,borderRadius:10,padding:"6px 12px",color:shuffle?th.accent:th.mutedColor,cursor:"pointer",fontSize:"0.74rem",fontWeight:700,fontFamily:"'DM Sans',sans-serif",transition:"all 0.2s"}}
            >⇄ Shuffle</button>
            <button
              onClick={onClose}
              style={{background:"rgba(255,255,255,0.08)",border:"none",borderRadius:10,width:36,height:36,color:th.textColor,cursor:"pointer",fontSize:"1.1rem",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}
            >✕</button>
          </div>
        </div>

        <div style={{flex:1,overflowY:"auto",padding:"0 24px 20px",display:"flex",flexDirection:"column",gap:12}}>
          <div
            onDrop={handleDrop}
            onDragOver={e=>{e.preventDefault();setDragging(true);}}
            onDragLeave={()=>setDragging(false)}
            style={{border:`2px dashed ${dragging?th.accent:`${th.accent}40`}`,borderRadius:16,padding:"16px 20px",textAlign:"center",background:dragging?`${th.accent}12`:"rgba(255,255,255,0.03)",transition:"all 0.2s",cursor:"pointer"}}
            onClick={()=>document.getElementById("fr-music-input").click()}
          >
            <div style={{fontSize:26,marginBottom:4}}>{dragging?"📂":"🎵"}</div>
            <div style={{color:th.textColor,fontWeight:700,fontSize:"0.82rem",marginBottom:2}}>{dragging?"Drop it here!":"Drop audio files or click to browse"}</div>
            <div style={{color:th.mutedColor,fontSize:"0.68rem"}}>MP3, WAV, OGG, FLAC — stays on your device</div>
            <input id="fr-music-input" type="file" accept="audio/*" multiple style={{display:"none"}} onChange={e=>handleFiles(e.target.files)}/>
          </div>

          {ct && (
            <div style={{...glass,padding:"16px 20px"}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                <MusicBars accent={th.accent} playing={isPlaying}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{color:th.textColor,fontWeight:700,fontSize:"0.9rem",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{ct.name}</div>
                  <div style={{color:th.mutedColor,fontSize:"0.68rem",marginTop:2}}>Track {currentIdx+1} of {tracks.length}</div>
                </div>
              </div>

              <div onClick={seek} style={{position:"relative",height:5,background:"rgba(255,255,255,0.1)",borderRadius:3,cursor:"pointer",marginBottom:5}}>
                <div style={{position:"absolute",top:0,left:0,bottom:0,width:`${pct}%`,borderRadius:3,background:`linear-gradient(90deg,${th.accent},${th.accentGlow})`,transition:"width 0.3s"}}/>
                <div style={{position:"absolute",top:"50%",left:`${pct}%`,transform:"translate(-50%,-50%)",width:13,height:13,borderRadius:"50%",background:"#fff",boxShadow:`0 0 8px ${th.accent}80`,pointerEvents:"none"}}/>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:14}}>
                <span style={{color:th.mutedColor,fontSize:"0.65rem"}}>{fmt(progress)}</span>
                <span style={{color:th.mutedColor,fontSize:"0.65rem"}}>{fmt(duration)}</span>
              </div>

              <div style={{display:"flex",gap:12,alignItems:"center",justifyContent:"center",marginBottom:12}}>
                <button onClick={prev} disabled={tracks.length<2} style={{background:"rgba(255,255,255,0.08)",border:"none",borderRadius:10,width:42,height:42,color:th.textColor,cursor:"pointer",fontSize:"1.1rem",opacity:tracks.length<2?0.3:1,transition:"all 0.2s"}}>⏮</button>
                <button onClick={togglePlay} style={{background:`linear-gradient(135deg,${th.accent},${th.accentGlow})`,border:"none",borderRadius:50,width:54,height:54,color:"#fff",cursor:"pointer",fontSize:"1.4rem",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 6px 24px ${th.accent}60`,transition:"all 0.2s"}}>{isPlaying?"⏸":"▶"}</button>
                <button onClick={next} disabled={tracks.length<2} style={{background:"rgba(255,255,255,0.08)",border:"none",borderRadius:10,width:42,height:42,color:th.textColor,cursor:"pointer",fontSize:"1.1rem",opacity:tracks.length<2?0.3:1,transition:"all 0.2s"}}>⏭</button>
              </div>

              <VolumeSlider value={volume} onChange={setVolume} accent={th.accent}/>
            </div>
          )}

          {tracks.length > 0 && (
            <div style={{...glass,padding:"12px 16px"}}>
              <div style={{color:th.mutedColor,fontSize:"0.6rem",fontWeight:800,textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:8}}>Playlist</div>
              <div style={{display:"flex",flexDirection:"column",gap:3,maxHeight:180,overflowY:"auto"}}>
                {tracks.map((t,i)=>(
                  <div key={i} onClick={()=>playTrack(i)} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:10,background:i===currentIdx?`${th.accent}20`:"transparent",border:`1px solid ${i===currentIdx?th.accent+"50":"transparent"}`,cursor:"pointer",transition:"all 0.15s"}}>
                    <span style={{fontSize:"0.7rem",color:th.mutedColor,minWidth:18,textAlign:"center",fontWeight:700}}>{i===currentIdx&&isPlaying?"▶":i+1}</span>
                    <span style={{color:i===currentIdx?th.accent:th.textColor,fontSize:"0.82rem",fontWeight:i===currentIdx?700:400,flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.name}</span>
                    <button onClick={e=>removeTrack(i,e)} style={{background:"none",border:"none",color:th.mutedColor,cursor:"pointer",fontSize:"0.75rem",opacity:0.5,padding:"0 2px",flexShrink:0}}>✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <audio ref={audioRef} onTimeUpdate={handleTimeUpdate} onEnded={next} onPlay={()=>setIsPlaying(true)} onPause={()=>setIsPlaying(false)} style={{display:"none"}}/>
      </div>
    </>
  );
}

// ─── MAIN FOCUS ROOM ──────────────────────────────────────────────────────────
//export default function FocusRoom({ isEmbeddedFullscreen = false }) {
export default function FocusRoom({ isEmbeddedFullscreen = false, onClose }) {
  const [activeTheme,    setActiveTheme]    = useState(THEMES[0]);
  const [volume,         setVolume]         = useState(0.5);
  const [duration,       setDuration]       = useState(25);
  const [customMins,     setCustomMins]     = useState("");
  const [timerState,     setTimerState]     = useState("idle");
  const [timeLeft,       setTimeLeft]       = useState(25 * 60);
  const [sessions,       setSessions]       = useState(() => { try { return parseInt(localStorage.getItem("fr_sess")||"0"); } catch { return 0; } });
  const [totalMins,      setTotalMins]      = useState(() => { try { return parseInt(localStorage.getItem("fr_mins")||"0"); } catch { return 0; } });
  const [showControls,   setShowControls]   = useState(true);
  const [showMusicSheet, setShowMusicSheet] = useState(false);
  const [musicPlaying,   setMusicPlaying]   = useState(false);
  const [ambientEnabled, setAmbientEnabled] = useState(true);
  const [activeSound,    setActiveSound]    = useState(THEMES[0].soundId);

  const synthRef     = useRef(null);
  const intervalRef  = useRef(null);
  const remainingRef = useRef(25 * 60);

  function stopAmbient() {
    if (synthRef.current) {
      try { synthRef.current.stop(); } catch(e) {}
      synthRef.current = null;
    }
  }

  function startAmbient(soundId, vol) {
    stopAmbient();
    const fn = SYNTH_MAP[soundId];
    if (!fn) return;
    try { synthRef.current = fn(vol); } catch(e) { synthRef.current = null; }
  }

  function chime() {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      [523, 659, 784, 1047].forEach((freq, i) => {
        const osc = ctx.createOscillator(), g = ctx.createGain();
        osc.connect(g); g.connect(ctx.destination);
        osc.type = "sine"; osc.frequency.value = freq;
        g.gain.setValueAtTime(0, ctx.currentTime + i * 0.3);
        g.gain.linearRampToValueAtTime(0.3, ctx.currentTime + i * 0.3 + 0.06);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.3 + 1);
        osc.start(ctx.currentTime + i * 0.3);
        osc.stop(ctx.currentTime + i * 0.3 + 1.1);
      });
    } catch(e) {}
  }

  function tick(sessRef, totRef, durRef) {
    remainingRef.current -= 1;
    setTimeLeft(remainingRef.current);
    if (remainingRef.current <= 0) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      const ns = sessRef.current + 1;
      const nm = totRef.current + durRef.current;
      sessRef.current = ns;
      totRef.current  = nm;
      setSessions(ns);
      setTotalMins(nm);
      try { localStorage.setItem("fr_sess", String(ns)); } catch(e) {}
      try { localStorage.setItem("fr_mins", String(nm)); } catch(e) {}
      stopAmbient();
      chime();
      setTimerState("done");
    }
  }

  const sessRef = useRef(sessions);
  const totRef  = useRef(totalMins);
  const durRef  = useRef(duration);

  useEffect(() => { durRef.current = duration; }, [duration]);

  function handleStart() {
    const startFrom = timerState === "done" ? duration * 60 : remainingRef.current;
    remainingRef.current = startFrom;
    setTimeLeft(startFrom);
    setTimerState("running");
    if (ambientEnabled && !musicPlaying) {
      startAmbient(activeSound, volume);
    }
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => tick(sessRef, totRef, durRef), 1000);
  }

  function handlePause() {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    stopAmbient();
    setTimerState("paused");
  }

  function handleReset() {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    stopAmbient();
    remainingRef.current = duration * 60;
    setTimeLeft(duration * 60);
    setTimerState("idle");
  }

  function setDur(m) {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    stopAmbient();
    setDuration(m);
    durRef.current = m;
    remainingRef.current = m * 60;
    setTimeLeft(m * 60);
    setTimerState("idle");
  }

  function handleThemeChange(theme) {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    stopAmbient();
    remainingRef.current = duration * 60;
    setTimeLeft(duration * 60);
    setTimerState("idle");
    setActiveTheme(theme);
    setActiveSound(theme.soundId);
    setShowControls(false);
  }

  function handleSoundChange(soundId) {
    setActiveSound(soundId);
    if (timerState === "running" && ambientEnabled && !musicPlaying) {
      startAmbient(soundId, volume);
    }
  }

  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current);
      stopAmbient();
    };
  }, []);

  const isRunning  = timerState === "running";
  const isPaused   = timerState === "paused";
  const isDone     = timerState === "done";
  const hasStarted = isRunning || isPaused;
  const th         = activeTheme;
  const PRESETS    = [{label:"5m",mins:5},{label:"10m",mins:10},{label:"15m",mins:15},{label:"25m",mins:25},{label:"45m",mins:45},{label:"60m",mins:60},{label:"90m",mins:90}];
  const glass      = {background:th.cardBg,backdropFilter:"blur(24px)",WebkitBackdropFilter:"blur(24px)",border:`1px solid ${th.borderColor}`,borderRadius:20};

  return (
    <div
      style={{
        // 🔥 Absolute positioning overrides the PWA tab padding, ensuring the background covers the whole screen
        position: isEmbeddedFullscreen ? "relative" : "absolute",
        top: 0, bottom: 0, left: 0, right: 0,
        width: "100%", height: "100%",
        minHeight: isEmbeddedFullscreen ? "auto" : 640,
        overflow: "hidden",
        fontFamily: "'DM Sans', sans-serif",
        background: th.bg,
        transition: "background 1.2s ease",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{SCENE_CSS+`
        .fr-drawer{transition:opacity 0.45s ease,transform 0.45s cubic-bezier(0.22,1,0.36,1),max-height 0.45s cubic-bezier(0.22,1,0.36,1);}
        .fr-drawer.closed{opacity:0;transform:translateY(24px);max-height:0!important;overflow:hidden;pointer-events:none;}
        .fr-drawer.open{opacity:1;transform:translateY(0);}
        .fr-theme-btn{transition:all 0.2s;} .fr-theme-btn:hover{transform:scale(1.06) translateY(-2px);}
        .fr-preset-btn{transition:all 0.15s;} .fr-preset-btn:hover{transform:scale(1.05);}
        .fr-toggle-pill:hover{opacity:1!important;transform:scale(1.04);}
        .fr-music-btn:hover{transform:translateY(-1px);filter:brightness(1.1);}
      `}</style>

      {/* Background scene */}
      <div style={{position:"absolute",inset:0,zIndex:0,pointerEvents:"none"}}><ThemeScene theme={th}/></div>
      {/* Vignette */}
      <div style={{position:"absolute",inset:0,zIndex:1,pointerEvents:"none",background:"radial-gradient(ellipse at 50% 45%, transparent 35%, rgba(0,0,0,0.38) 100%)"}}/>

      {/* 🔥 Top Bar: CSS variables ensure it's pushed down past the PWA header automatically! */}
      <div style={{
        position:"relative", zIndex:10, display:"flex",
        justifyContent:"space-between",
        alignItems:"center",
        paddingTop: "calc(var(--topbar-h, 48px) + var(--safe-top, 0px) + 14px)",
        paddingBottom: "14px",
        paddingLeft: "24px",
        paddingRight: "24px",
        background:"linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, transparent 100%)",
        flexShrink:0
      }}>
        
        {/* 👈 Added Back Button */}
        <button
          onClick={() => {
            // If the parent passed an onClose function, use it!
            if (onClose) {
              onClose();
            } 
            // Fallback for standard routing
            else if (typeof window !== 'undefined') {
               window.history.length > 1 ? window.history.back() : window.location.href = '/';
            }
          }}
          style={{
            background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)",
            borderRadius:12, width:40, height:40, color:"#fff", cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:"1.2rem", backdropFilter:"blur(10px)", zIndex:50,
            flexShrink: 0
          }}
          aria-label="Close Focus Room"
        >
          ✕
        </button>

        <div style={{position:"absolute",left:"50%",top:`calc(50% + var(--topbar-h, 48px)/2 + var(--safe-top, 0px)/2)`,transform:"translate(-50%,-50%)",display:"flex",alignItems:"center",gap:10,pointerEvents:"none"}}>
          <span style={{fontSize:24,filter:`drop-shadow(0 0 10px ${th.accentGlow})`}}>{th.emoji}</span>
          <div>
            <div style={{fontWeight:800,color:th.textColor,fontSize:"1rem",letterSpacing:"-0.02em",textShadow:"0 2px 8px rgba(0,0,0,0.8)"}}>{th.label}</div>
            <div style={{color:th.mutedColor,fontSize:"0.65rem",fontStyle:"italic"}}>{th.tagline}</div>
          </div>
        </div>
        
        <div style={{...glass,padding:"7px 16px",borderRadius:50,display:"flex",alignItems:"center",gap:8, flexShrink: 0}}>
          <span>🔥</span>
          <span style={{color:th.accent,fontWeight:700,fontSize:"0.8rem"}}>{sessions} sessions</span>
        </div>
      </div>

      {/* Timer zone */}
      <div style={{position:"relative",zIndex:20,flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:24,padding:"20px 24px 16px",minHeight:0}}>
        <CircularTimer timeLeft={timeLeft} totalSeconds={duration*60} accent={th.accent} accentGlow={th.accentGlow} size={240} textColor={th.textColor} mutedColor={th.mutedColor}/>

        {/* Start / Pause / Reset */}
        <div style={{display:"flex",gap:12,alignItems:"center",justifyContent:"center",position:"relative",zIndex:30}}>
          {!isRunning && (
            <button
              onClick={handleStart}
              style={{background:`linear-gradient(135deg,${th.accent},${th.accentGlow})`,color:"#fff",border:"none",borderRadius:50,padding:"13px 38px",fontWeight:800,fontSize:"1rem",cursor:"pointer",boxShadow:`0 8px 30px ${th.accent}60,0 2px 10px rgba(0,0,0,0.5)`,letterSpacing:"0.04em",fontFamily:"'DM Sans',sans-serif",position:"relative",zIndex:30}}
              onMouseEnter={e=>{e.currentTarget.style.transform="scale(1.05) translateY(-2px)";}}
              onMouseLeave={e=>{e.currentTarget.style.transform="none";}}
            >
              ▶ {isPaused?"Resume":isDone?"Again":"Start"}
            </button>
          )}
          {isRunning && (
            <button
              onClick={handlePause}
              style={{...glass,color:th.textColor,border:`1.5px solid ${th.accent}55`,borderRadius:50,padding:"13px 32px",fontWeight:800,cursor:"pointer",fontSize:"0.95rem",fontFamily:"'DM Sans',sans-serif",position:"relative",zIndex:30}}
            >⏸ Pause</button>
          )}
          {hasStarted && (
            <button
              onClick={handleReset}
              style={{...glass,color:th.mutedColor,borderRadius:50,padding:"13px 18px",fontWeight:700,cursor:"pointer",fontSize:"1.1rem",fontFamily:"'DM Sans',sans-serif",position:"relative",zIndex:30}}
            >↺</button>
          )}
        </div>

        {/* Sound status pill */}
        {isRunning && (
          <div style={{display:"flex",alignItems:"center",gap:8,opacity:0.8,position:"relative",zIndex:30}}>
            <MusicBars accent={th.accent} playing={true}/>
            <span style={{color:th.mutedColor,fontSize:"0.68rem",fontWeight:600}}>
              {musicPlaying?"🎵 Your music playing":SOUND_LABELS[activeSound]}
              <span style={{opacity: 0.7, marginLeft: 8}}>• 🎧 Headphones recommended</span>
            </span>
            {!musicPlaying && (
              <button onClick={()=>setAmbientEnabled(a=>!a)} style={{background:"none",border:`1px solid ${th.accent}40`,borderRadius:8,padding:"2px 8px",color:ambientEnabled?th.accent:th.mutedColor,cursor:"pointer",fontSize:"0.62rem",fontWeight:700,fontFamily:"'DM Sans',sans-serif"}}>
                {ambientEnabled?"ON":"OFF"}
              </button>
            )}
          </div>
        )}

        {/* Quote */}
        {!isDone && (
          <div style={{...glass,padding:"14px 22px",maxWidth:400,width:"100%",textAlign:"center",animation:"fadeInUp 0.7s 0.15s ease both"}}>
            <p style={{fontFamily:"'DM Serif Display',serif",fontSize:"0.85rem",fontStyle:"italic",color:th.textColor,margin:0,lineHeight:1.75,opacity:0.88}}>"{th.quote}"</p>
            <p style={{color:th.accent,fontSize:"0.7rem",fontWeight:700,marginTop:7,opacity:0.85}}>— {th.quoteAuthor}</p>
          </div>
        )}

        {/* Done banner */}
        {isDone && (
          <div style={{...glass,padding:"22px 36px",textAlign:"center",animation:"panelReveal 0.5s ease",border:`1.5px solid ${th.accent}55`}}>
            <div style={{fontSize:46,marginBottom:8}}>🎉</div>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:"1.3rem",color:th.textColor,marginBottom:4}}>Session Complete!</div>
            <div style={{color:th.mutedColor,fontSize:"0.78rem",marginBottom:14}}>{duration} minutes of pure focus.</div>
            <button onClick={handleReset} style={{background:`${th.accent}28`,color:th.accent,border:`1px solid ${th.accent}55`,borderRadius:12,padding:"9px 22px",fontWeight:700,cursor:"pointer",fontSize:"0.86rem",fontFamily:"'DM Sans',sans-serif"}}>↺ Go again</button>
          </div>
        )}
      </div>

      {/* 🔥 Settings Toggle Pill: Automatically pads above the PWA bottom tab bar! */}
      <div style={{
        position: "relative",
        zIndex: 25,
        display: "flex",
        justifyContent: "center",
        paddingBottom: showControls 
          ? "calc(var(--tab-h, 56px) + var(--safe-bottom, 0px) + 12px)" 
          : "calc(var(--tab-h, 56px) + var(--safe-bottom, 0px) + 24px)",
        flexShrink: 0,
      }}>
        <button
          className="fr-toggle-pill"
          onClick={()=>setShowControls(s=>!s)}
          style={{
            ...glass,
            border:`1px solid ${th.accent}40`,
            color:th.textColor,
            padding:"8px 22px",
            borderRadius:50,
            cursor:"pointer",
            fontFamily:"'DM Sans',sans-serif",
            fontSize:"0.76rem",
            fontWeight:700,
            letterSpacing:"0.06em",
            display:"flex",
            alignItems:"center",
            gap:8,
            opacity:0.82,
            boxShadow:`0 4px 20px rgba(0,0,0,0.3)`,
          }}
        >
          <span style={{display:"inline-block",transition:"transform 0.3s ease",transform:showControls?"rotate(180deg)":"rotate(0deg)",fontSize:"0.7rem"}}>▲</span>
          {showControls?"Hide Controls":"⚙ Settings"}
        </button>
      </div>

      {/* Settings Drawer */}
      <div
        className={`fr-drawer ${showControls?"open":"closed"}`}
        style={{
          position:"relative",
          zIndex:24,
          maxHeight:showControls?460:0,
          background:"linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.6) 100%)",
          backdropFilter:"blur(20px)",
          WebkitBackdropFilter:"blur(20px)",
          flexShrink:0,
          overflowY:"auto",
        }}
      >
        <div style={{
          padding: "16px 24px",
          // 🔥 Bottom padding allows you to scroll to the very end of the themes grid
          paddingBottom: "calc(var(--tab-h, 56px) + var(--safe-bottom, 0px) + 24px)",
          maxWidth: 960,
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>

            {/* Duration */}
            <div style={{...glass,padding:"14px 16px"}}>
              <div style={{color:th.mutedColor,fontSize:"0.6rem",fontWeight:800,textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:10}}>⏱ Duration</div>
              <div style={{display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
                {PRESETS.map(p=>(
                  <button key={p.mins} className="fr-preset-btn" onClick={()=>setDur(p.mins)} style={{padding:"5px 10px",borderRadius:8,fontSize:"0.74rem",fontWeight:700,border:`1.5px solid ${duration===p.mins?th.accent:"rgba(255,255,255,0.13)"}`,background:duration===p.mins?`${th.accent}30`:"rgba(255,255,255,0.06)",color:duration===p.mins?th.accent:th.mutedColor,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>{p.label}</button>
                ))}
                <input type="number" min={1} max={240} placeholder="Custom…" value={customMins} onChange={e=>setCustomMins(e.target.value)} style={{width:70,background:"rgba(255,255,255,0.08)",border:`1px solid ${th.borderColor}`,borderRadius:8,padding:"5px 8px",color:th.textColor,fontSize:"0.74rem",outline:"none",fontFamily:"'DM Sans',sans-serif"}}/>
                <button onClick={()=>{const m=parseInt(customMins);if(m>0&&m<=240)setDur(m);}} style={{background:`${th.accent}22`,color:th.accent,border:`1px solid ${th.accent}50`,borderRadius:8,padding:"5px 11px",fontWeight:700,fontSize:"0.74rem",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>Set</button>
              </div>
            </div>

            {/* Sound */}
            <div style={{...glass,padding:"14px 16px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{color:th.mutedColor,fontSize:"0.6rem",fontWeight:800,textTransform:"uppercase",letterSpacing:"0.12em"}}>🎵 Ambient Sound</div>
                <button onClick={()=>setAmbientEnabled(a=>!a)} style={{background:ambientEnabled?`${th.accent}28`:"rgba(255,255,255,0.06)",border:`1.5px solid ${ambientEnabled?th.accent:"rgba(255,255,255,0.12)"}`,borderRadius:8,padding:"3px 10px",color:ambientEnabled?th.accent:th.mutedColor,cursor:"pointer",fontSize:"0.68rem",fontWeight:700,fontFamily:"'DM Sans',sans-serif"}}>{ambientEnabled?"ON":"OFF"}</button>
              </div>
              <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:10}}>
                {Object.entries(SOUND_LABELS).map(([id, label]) => (
                  <button key={id} onClick={()=>handleSoundChange(id)} style={{padding:"5px 9px",borderRadius:8,fontSize:"0.68rem",fontWeight:700,border:`1.5px solid ${activeSound===id?th.accent:"rgba(255,255,255,0.12)"}`,background:activeSound===id?`${th.accent}30`:"rgba(255,255,255,0.05)",color:activeSound===id?th.accent:th.mutedColor,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>{label}</button>
                ))}
              </div>
              <VolumeSlider value={volume} onChange={(v) => {
                setVolume(v);
                if (synthRef.current && synthRef.current.setVol) synthRef.current.setVol(v);
              }} accent={th.accent}/>
              <div style={{borderTop:`1px solid ${th.borderColor}`,marginTop:10,paddingTop:10}}>
                <button
                  className="fr-music-btn"
                  onClick={()=>setShowMusicSheet(true)}
                  style={{width:"100%",background:musicPlaying?`${th.accent}22`:"rgba(255,255,255,0.07)",border:`1.5px solid ${musicPlaying?th.accent:"rgba(255,255,255,0.15)"}`,borderRadius:12,padding:"10px 16px",color:musicPlaying?th.accent:th.textColor,cursor:"pointer",fontWeight:700,fontSize:"0.8rem",fontFamily:"'DM Sans',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}
                >
                  {musicPlaying?(<><MusicBars accent={th.accent} playing={true}/><span>My Music Playing</span></>):<span>🎵 My Music</span>}
                </button>
              </div>
            </div>
          </div>

          {/* Themes */}
          <div style={{...glass,padding:"14px 16px"}}>
            <div style={{color:th.mutedColor,fontSize:"0.6rem",fontWeight:800,textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:10}}>🎨 Choose Your Room</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(10,1fr)",gap:7}}>
              {THEMES.map(theme=>(
                <button key={theme.id} className="fr-theme-btn" onClick={()=>handleThemeChange(theme)} style={{padding:"9px 4px",borderRadius:11,cursor:"pointer",border:`2px solid ${activeTheme.id===theme.id?theme.accent:"rgba(255,255,255,0.08)"}`,background:activeTheme.id===theme.id?`${theme.accent}28`:"rgba(255,255,255,0.04)",display:"flex",flexDirection:"column",alignItems:"center",gap:4,fontFamily:"'DM Sans',sans-serif",boxShadow:activeTheme.id===theme.id?`0 4px 18px ${theme.accent}40`:"none"}}>
                  <span style={{fontSize:19,lineHeight:1,filter:activeTheme.id===theme.id?`drop-shadow(0 0 6px ${theme.accentGlow})`:"none"}}>{theme.emoji}</span>
                  <span style={{fontSize:"0.56rem",fontWeight:700,lineHeight:1.2,textAlign:"center",color:activeTheme.id===theme.id?theme.accent:"rgba(255,255,255,0.38)"}}>{theme.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showMusicSheet && (
        <LocalMusicSheet
          theme={th}
          onClose={()=>setShowMusicSheet(false)}
          onPlayStateChange={(playing) => {
            setMusicPlaying(playing);
            if (playing) stopAmbient();
            else if (timerState === "running" && ambientEnabled) startAmbient(activeSound, volume);
          }}
        />
      )}
    </div>
  );
}